// This file is auto-generated by @hey-api/openapi-ts

import * as z from 'zod';

/**
 * AccessLevel
 */
export const zAccessLevel = z.enum([
    'single',
    'automatic',
    'minimum',
    'negotiated'
]);

/**
 * ActiveJobCount
 */
export const zActiveJobCount = z.object({
    queued: z.int(),
    running: z.int(),
    runs_outside_jobs: z.int()
});

/**
 * ActiveSession
 */
export const zActiveSession = z.object({
    created_at: z.iso.datetime({ offset: true }),
    current: z.boolean(),
    ip: z.string().nullable(),
    last_seen_at: z.iso.datetime({ offset: true }),
    session_id: z.string(),
    user_agent: z.string().nullable()
});

/**
 * ActivityItem
 */
export const zActivityItem = z.object({
    at: z.iso.datetime({ offset: true }),
    detail: z.string().nullable(),
    icon: z.enum([
        'scan',
        'db',
        'file',
        'compare'
    ]),
    kind: z.enum([
        'ingestion',
        'job',
        'watch'
    ]),
    link: z.string().nullable(),
    status: z.string(),
    title: z.string()
});

/**
 * Actor
 */
export const zActor = z.object({
    key: z.string(),
    kind: z.enum([
        'user',
        'system',
        'seed',
        'cli'
    ]),
    name: z.string()
});

/**
 * ActorRef
 */
export const zActorRef = z.object({
    name: z.string(),
    user_id: z.string()
});

/**
 * AdapterInfo
 */
export const zAdapterInfo = z.object({
    adapter_id: z.string(),
    adapter_version: z.string(),
    description: z.string(),
    kind: z.enum(['generic', 'publisher']),
    label: z.string()
});

/**
 * AnswerScope
 */
export const zAnswerScope = z.object({
    as_of: z.iso.date().nullish(),
    clause_uid: z.string().nullish(),
    control_functions: z.array(z.string()).max(50).nullish(),
    doc_uids: z.array(z.string()).nullish(),
    jurisdiction: z.string().nullish(),
    priorities: z.array(z.enum([
        'P1',
        'P2',
        'P3'
    ])).max(3).nullish(),
    req_uid: z.string().nullish(),
    topics: z.array(z.string()).max(50).nullish()
});

/**
 * AnswerRequest
 */
export const zAnswerRequest = z.object({
    question: z.string().min(1).max(4000),
    scope: zAnswerScope.optional(),
    thread_id: z.string().min(1).max(64).nullish()
});

/**
 * ApiMeta
 */
export const zApiMeta = z.object({
    assistant_model: z.string().nullable(),
    building_documents: z.array(z.string()),
    confidential_ai_enabled: z.boolean(),
    data_version: z.string(),
    embeddings_configured: z.boolean(),
    environment: z.string().nullable(),
    git_sha: z.string().nullable(),
    job_launcher: z.enum([
        'local',
        'worker',
        'aca',
        'none'
    ]),
    llm_configured: z.boolean(),
    llm_provider: z.string().nullable(),
    read_only: z.boolean(),
    rerank_enabled: z.boolean(),
    schema_version: z.string().nullable(),
    upload_max_bytes: z.int(),
    upload_part_bytes: z.int(),
    version: z.string(),
    workers_online: z.int().nullable()
});

/**
 * AssessmentApproval
 */
export const zAssessmentApproval = z.object({
    comment: z.string().max(2000).nullish()
});

/**
 * AssessmentProduct
 */
export const zAssessmentProduct = z.object({
    product_types: z.array(z.string()),
    title: z.string(),
    uid: z.string()
});

/**
 * AssessmentReopen
 */
export const zAssessmentReopen = z.object({
    comment: z.string().min(1).max(2000)
});

/**
 * AssessmentRerun
 */
export const zAssessmentRerun = z.object({
    topic_keys: z.array(z.string().regex(/^[a-z][a-z0-9_]*$/)).optional()
});

/**
 * AssessmentRunRequest
 */
export const zAssessmentRunRequest = z.object({
    assessment_uid: z.string().regex(/^ASM-\d{8}-[0-9a-f]{6}$/),
    kind: z.literal('assessment_run'),
    topic_keys: z.array(z.string().regex(/^[a-z][a-z0-9_]*$/)).optional()
});

/**
 * AssessmentStatus
 */
export const zAssessmentStatus = z.enum([
    'queued',
    'running',
    'ready',
    'approved',
    'failed',
    'cancelled'
]);

/**
 * AssistantBudgetOut
 */
export const zAssistantBudgetOut = z.object({
    answers: z.int(),
    custom: z.boolean(),
    limit_usd: z.number(),
    month: z.string(),
    remaining_usd: z.number(),
    renews_at: z.iso.datetime({ offset: true }),
    used_usd: z.number()
});

/**
 * AssistantBudgetUpdate
 */
export const zAssistantBudgetUpdate = z.object({
    limit_usd: z.number().gt(0).lte(100000).nullable()
});

/**
 * AssistantCitation
 */
export const zAssistantCitation = z.object({
    cited_text: z.string(),
    clause_uid: z.string(),
    number: z.string(),
    page_start: z.int(),
    source_index: z.int()
});

/**
 * AssistantBlock
 */
export const zAssistantBlock = z.object({
    citations: z.array(zAssistantCitation),
    index: z.int(),
    text: z.string()
});

/**
 * AssistantCitationEvent
 */
export const zAssistantCitationEvent = z.object({
    block_index: z.int(),
    cited_text: z.string(),
    clause_uid: z.string(),
    end_block_index: z.int(),
    event: z.literal('citation').default('citation'),
    number: z.string(),
    page_start: z.int(),
    source_index: z.int(),
    start_block_index: z.int()
});

/**
 * AssistantDeltaEvent
 */
export const zAssistantDeltaEvent = z.object({
    block_index: z.int(),
    event: z.literal('delta').default('delta'),
    text: z.string()
});

/**
 * AssistantDoneEvent
 */
export const zAssistantDoneEvent = z.object({
    citation_count: z.int(),
    event: z.literal('done').default('done'),
    grounded: z.boolean(),
    refusal_category: z.string().nullable(),
    stop_reason: z.string()
});

/**
 * AssistantMetaEvent
 */
export const zAssistantMetaEvent = z.object({
    answer_id: z.string(),
    event: z.literal('meta').default('meta'),
    llm_configured: z.boolean(),
    model: z.string().nullable(),
    notice: z.string().nullable(),
    provider: z.string().nullable(),
    question_id: z.string(),
    scope: zAnswerScope,
    thread_id: z.string()
});

/**
 * AssistantNotice
 */
export const zAssistantNotice = z.object({
    kind: z.enum(['fallback', 'search_degraded']),
    message: z.string()
});

/**
 * AssistantNoticeEvent
 */
export const zAssistantNoticeEvent = z.object({
    event: z.literal('notice').default('notice'),
    kind: z.enum(['fallback', 'search_degraded']),
    message: z.string()
});

/**
 * AssistantOutcome
 */
export const zAssistantOutcome = z.object({
    citation_count: z.int(),
    grounded: z.boolean(),
    refusal_category: z.string().nullable(),
    stop_reason: z.string()
});

/**
 * AssistantStatusEvent
 */
export const zAssistantStatusEvent = z.object({
    event: z.literal('status').default('status'),
    phase: z.enum([
        'retrieving',
        'thinking',
        'writing'
    ])
});

/**
 * AssistantThread
 */
export const zAssistantThread = z.object({
    created_at: z.iso.datetime({ offset: true }),
    message_count: z.int(),
    scope: zAnswerScope,
    thread_id: z.string(),
    title: z.string(),
    updated_at: z.iso.datetime({ offset: true })
});

/**
 * AssistantUsage
 */
export const zAssistantUsage = z.object({
    cache_read_tokens: z.int(),
    cache_write_tokens: z.int(),
    cost_usd: z.number(),
    fallback_used: z.boolean(),
    input_tokens: z.int(),
    output_tokens: z.int(),
    served_model: z.string()
});

/**
 * AssistantUsageEvent
 */
export const zAssistantUsageEvent = z.object({
    cache_read_tokens: z.int(),
    cache_write_tokens: z.int(),
    cost_usd: z.number(),
    event: z.literal('usage').default('usage'),
    fallback_used: z.boolean(),
    input_tokens: z.int(),
    output_tokens: z.int(),
    request_id: z.string().nullable(),
    served_model: z.string()
});

/**
 * AuditVerification
 */
export const zAuditVerification = z.object({
    anchors_checked: z.int().gte(0).default(0),
    checked: z.int().gte(0),
    first_invalid_seq: z.int().nullable(),
    head_hash: z.string(),
    unmatched_anchor_seq: z.int().nullable(),
    valid: z.boolean()
});

/**
 * AuthEventOutcome
 */
export const zAuthEventOutcome = z.enum(['success', 'failure']);

/**
 * AuthEventType
 */
export const zAuthEventType = z.enum([
    'sign_in_succeeded',
    'sign_in_failed',
    'account_locked',
    'signed_out',
    'password_changed',
    'password_reset_requested',
    'password_reset_completed',
    'invitation_created',
    'invitation_resent',
    'invitation_revoked',
    'invitation_accepted',
    'user_roles_changed',
    'user_disabled',
    'user_enabled',
    'sessions_revoked',
    'mfa_enrolled',
    'mfa_failed',
    'recovery_code_used',
    'recovery_codes_regenerated',
    'mfa_reset',
    'assistant_budget_changed'
]);

/**
 * AuthEvent
 */
export const zAuthEvent = z.object({
    actor_email: z.string().nullable(),
    actor_user_id: z.string().nullable(),
    at: z.iso.datetime({ offset: true }),
    detail: z.string().nullable(),
    event_id: z.string(),
    ip: z.string().nullable(),
    outcome: zAuthEventOutcome,
    target_email: z.string().nullable(),
    type: zAuthEventType,
    user_agent: z.string().nullable()
});

/**
 * AuthOptions
 */
export const zAuthOptions = z.object({
    email_enabled: z.boolean()
});

/**
 * AuthenticatorReplacement
 */
export const zAuthenticatorReplacement = z.object({
    current_password: z.string().min(1).max(1024)
});

/**
 * AuthorityKind
 */
export const zAuthorityKind = z.enum([
    'eu_body',
    'regulator',
    'system_operator',
    'tso',
    'dso',
    'standards_body',
    'other'
]);

/**
 * Authority
 */
export const zAuthority = z.object({
    acts_in: z.array(z.string()),
    authority_id: z.string(),
    kind: zAuthorityKind,
    name: z.string(),
    website: z.string().nullable()
});

/**
 * AuthorityIn
 */
export const zAuthorityIn = z.object({
    acts_in: z.array(z.string().max(32).regex(/^[A-Z][A-Z0-9]*(-[A-Z0-9]+)*$/)).optional(),
    authority_id: z.string().max(32).regex(/^[A-Z][A-Z0-9]*(-[A-Z0-9]+)*$/),
    kind: zAuthorityKind,
    name: z.string().min(1).max(300),
    website: z.url().min(1).max(2083).nullish()
});

/**
 * AuthorityOut
 */
export const zAuthorityOut = z.object({
    acts_in: z.array(z.string()),
    authority_id: z.string(),
    kind: zAuthorityKind,
    name: z.string(),
    website: z.string().nullable()
});

/**
 * AuthorityPatch
 */
export const zAuthorityPatch = z.object({
    acts_in: z.array(z.string().max(32).regex(/^[A-Z][A-Z0-9]*(-[A-Z0-9]+)*$/)).nullish(),
    kind: zAuthorityKind.nullish(),
    name: z.string().min(1).max(300).nullish(),
    website: z.url().min(1).max(2083).nullish()
});

/**
 * BaseQuantity
 */
export const zBaseQuantity = z.enum([
    'p_max',
    'p_n',
    's_n',
    'registered_capacity',
    'p_actual',
    'p_ref',
    'u_c',
    'u_n',
    'i_n',
    'f_n',
    'absolute',
    'not_applicable',
    'other'
]);

/**
 * BooleanFacet
 */
export const zBooleanFacet = z.object({
    false: z.int(),
    true: z.int()
});

/**
 * BuildState
 */
export const zBuildState = z.enum([
    'building',
    'ready',
    'deleting'
]);

/**
 * CancelJobRequest
 */
export const zCancelJobRequest = z.object({
    batches: z.enum(['cancel', 'keep']).optional().default('cancel')
});

/**
 * CapabilityCurveAxis
 */
export const zCapabilityCurveAxis = z.object({
    quantity: z.string(),
    unit: z.string()
});

/**
 * CapabilityCurvePoint
 */
export const zCapabilityCurvePoint = z.object({
    x: z.number(),
    y: z.number()
});

/**
 * CapabilityCurve
 */
export const zCapabilityCurve = z.object({
    name: z.string(),
    points: z.array(zCapabilityCurvePoint),
    x: zCapabilityCurveAxis,
    y: zCapabilityCurveAxis
});

/**
 * CapabilityDocumentStatus
 */
export const zCapabilityDocumentStatus = z.enum([
    'uploaded',
    'extracting',
    'ready',
    'failed'
]);

/**
 * CapabilityDocumentUse
 */
export const zCapabilityDocumentUse = z.object({
    assessment_uid: z.string(),
    name: z.string()
});

/**
 * CapabilityDocumentDetail
 */
export const zCapabilityDocumentDetail = z.object({
    byte_size: z.int(),
    capability_count: z.int().gte(0),
    capability_doc_uid: z.string(),
    controller: z.string().nullable(),
    cost_usd: z.number().nullable(),
    error: z.string().nullable(),
    extracted_at: z.iso.datetime({ offset: true }).nullable(),
    file_name: z.string(),
    job_id: z.string().nullable(),
    manufacturer: z.string().nullable(),
    model_version: z.string().nullable(),
    page_count: z.int().nullable(),
    product_types: z.array(z.string()),
    prompt_version: z.string().nullable(),
    scope_notes: z.string().nullable(),
    status: zCapabilityDocumentStatus,
    title: z.string().nullable(),
    uploaded_at: z.iso.datetime({ offset: true }),
    uploaded_by_name: z.string().nullable(),
    used_by: z.array(zCapabilityDocumentUse)
});

/**
 * CapabilityDocumentSummary
 */
export const zCapabilityDocumentSummary = z.object({
    capability_count: z.int().gte(0),
    capability_doc_uid: z.string(),
    error: z.string().nullable(),
    file_name: z.string(),
    job_id: z.string().nullable(),
    product_types: z.array(z.string()),
    status: zCapabilityDocumentStatus,
    title: z.string().nullable(),
    uploaded_at: z.iso.datetime({ offset: true }),
    uploaded_by_name: z.string().nullable(),
    used_by: z.array(zCapabilityDocumentUse)
});

/**
 * CapabilityExtractRequest
 */
export const zCapabilityExtractRequest = z.object({
    capability_doc_uid: z.string().regex(/^[0-9a-f]{64}$/),
    kind: z.literal('capability_extract')
});

/**
 * CapabilityKind
 */
export const zCapabilityKind = z.enum([
    'capability',
    'option',
    'not_available',
    'external_reference'
]);

/**
 * CapabilityLevel
 */
export const zCapabilityLevel = z.enum([
    'wtg',
    'ppc',
    'scada',
    'wind_farm'
]);

/**
 * CapabilityOut
 */
export const zCapabilityOut = z.object({
    applies_to: z.array(z.string()),
    capability_uid: z.string(),
    curves: z.array(zCapabilityCurve),
    entry_ref: z.string(),
    external_documents: z.array(z.string()),
    group_title: z.string(),
    kind: zCapabilityKind,
    levels: z.array(zCapabilityLevel),
    ordinal: z.int().gte(1),
    source_page: z.int().gte(1),
    statement: z.string(),
    title: z.string(),
    topic_keys: z.array(z.string())
});

/**
 * CapabilityUploadSession
 */
export const zCapabilityUploadSession = z.object({
    capability_document: zCapabilityDocumentDetail.nullable(),
    expires_at: z.iso.datetime({ offset: true }),
    file_name: z.string(),
    part_count: z.int(),
    part_size: z.int(),
    received: z.array(z.int()),
    size_bytes: z.int(),
    state: z.enum([
        'receiving',
        'completed',
        'aborted'
    ]),
    upload_id: z.string()
});

/**
 * ChartCitation
 */
export const zChartCitation = z.object({
    clause_number: z.string().nullable(),
    req_uid: z.string(),
    statement: z.string()
});

/**
 * ChartKind
 */
export const zChartKind = z.enum([
    'figure',
    'table',
    'text'
]);

/**
 * ChartTopic
 */
export const zChartTopic = z.enum([
    'frt',
    'frequency',
    'voltage',
    'reactive_power',
    'active_power',
    'power_quality',
    'other'
]);

/**
 * ChartSummary
 */
export const zChartSummary = z.object({
    chart_key: z.string(),
    cited_by_count: z.int(),
    clause_number: z.string().nullable(),
    clause_uid: z.string().nullable(),
    designation: z.string().nullable(),
    figure_key: z.string().nullable(),
    kind: zChartKind,
    line_count: z.int(),
    page: z.int().nullable(),
    priorities: z.array(z.enum([
        'P1',
        'P2',
        'P3'
    ])),
    title: z.string(),
    topic: zChartTopic
});

/**
 * CitedCapability
 */
export const zCitedCapability = z.object({
    capability_uid: z.string(),
    entry_ref: z.string(),
    kind: zCapabilityKind,
    levels: z.array(zCapabilityLevel),
    source_page: z.int(),
    statement: z.string(),
    title: z.string()
});

/**
 * CitedRequirement
 */
export const zCitedRequirement = z.object({
    clause_number: z.string().nullable(),
    low_confidence: z.boolean(),
    req_uid: z.string(),
    statement: z.string()
});

/**
 * ClauseChange
 */
export const zClauseChange = z.enum([
    'unchanged',
    'text_changed',
    'context_changed',
    'renumbered'
]);

/**
 * ClauseFlag
 */
export const zClauseFlag = z.enum([
    'obligation_gap',
    'informative_with_obligations',
    'extraction_failed',
    'extraction_refused',
    'requirement_invalid'
]);

/**
 * ClauseImpactKind
 */
export const zClauseImpactKind = z.enum([
    'text_changed',
    'context_changed',
    'renumbered',
    'removed'
]);

/**
 * ClauseImpact
 */
export const zClauseImpact = z.object({
    change: zClauseImpactKind,
    clause_uid: z.string(),
    new_clause_uid: z.string().nullable(),
    new_number: z.string().nullable(),
    number: z.string()
});

/**
 * ClauseKind
 */
export const zClauseKind = z.enum([
    'front_matter',
    'section',
    'heading',
    'clause',
    'item',
    'appendix',
    'schedule',
    'glossary'
]);

/**
 * ClauseRef
 */
export const zClauseRef = z.object({
    clause_uid: z.string(),
    kind: zClauseKind.nullable(),
    number: z.string(),
    title: z.string().nullable()
});

/**
 * ClauseContext
 */
export const zClauseContext = z.object({
    bbox: z.array(z.number()),
    clause_uid: z.string(),
    kind: zClauseKind,
    number: z.string(),
    page_end: z.int(),
    page_start: z.int(),
    path: z.array(zClauseRef),
    section_code: z.string(),
    section_title: z.string().nullable(),
    text: z.string(),
    title: z.string().nullable()
});

/**
 * CodeConfirmation
 */
export const zCodeConfirmation = z.object({
    code: z.string().min(1).max(64)
});

/**
 * Comparator
 */
export const zComparator = z.enum([
    'ge',
    'le',
    'gt',
    'lt',
    'eq',
    'between',
    'outside',
    'none'
]);

/**
 * ConditionDimension
 */
export const zConditionDimension = z.enum([
    'plant_type',
    'technology',
    'synchronous_area',
    'connection_voltage_kv',
    'connection_network',
    'user_category',
    'station_category',
    'capacity_mw',
    'offshore',
    'connection_date',
    'contract_date',
    'commissioning_date',
    'jurisdiction',
    'other'
]);

/**
 * ConditionNodeKind
 */
export const zConditionNodeKind = z.enum([
    'condition',
    'all',
    'any',
    'not'
]);

/**
 * ConditionOperator
 */
export const zConditionOperator = z.enum([
    'in',
    'not_in',
    'eq',
    'ne',
    'gt',
    'ge',
    'lt',
    'le',
    'between',
    'before',
    'on_or_after'
]);

/**
 * ConditionSource
 */
export const zConditionSource = z.enum([
    'extracted',
    'document_default',
    'manual'
]);

/**
 * ConditionLeaf
 */
export const zConditionLeaf = z.object({
    dimension: zConditionDimension.nullable(),
    number_max: z.number().nullable(),
    number_min: z.number().nullable(),
    operator: zConditionOperator.nullable(),
    sources: z.array(zConditionSource),
    unit: z.string().nullable(),
    values: z.array(z.string())
});

/**
 * ConditionNode
 */
export const zConditionNode = z.object({
    children: z.array(z.lazy((): any => zConditionNode)),
    condition_uid: z.string(),
    dimension: zConditionDimension.nullable(),
    node: zConditionNodeKind,
    number_max: z.number().nullable(),
    number_min: z.number().nullable(),
    operator: zConditionOperator.nullable(),
    source: zConditionSource,
    source_text: z.string().nullable(),
    unit: z.string().nullable(),
    values: z.array(z.string())
});

/**
 * ConfidenceBin
 */
export const zConfidenceBin = z.object({
    count: z.int(),
    lower: z.number(),
    upper: z.number()
});

/**
 * ConfidenceHistogram
 */
export const zConfidenceHistogram = z.object({
    below_threshold: z.int(),
    bins: z.array(zConfidenceBin),
    doc_uid: z.string(),
    threshold: z.number(),
    total: z.int()
});

/**
 * ConsolidateParametersJobRequest
 */
export const zConsolidateParametersJobRequest = z.object({
    dry_run: z.boolean().optional().default(true),
    kind: z.literal('consolidate_parameters'),
    report: z.literal(true).optional().default(true)
});

/**
 * ContextChange
 */
export const zContextChange = z.enum([
    'added',
    'removed',
    'changed'
]);

/**
 * ContextCounts
 */
export const zContextCounts = z.object({
    definitions: z.int(),
    figures: z.int(),
    stems: z.int(),
    tables: z.int()
});

/**
 * ContextDefinitionChange
 */
export const zContextDefinitionChange = z.object({
    change: zContextChange,
    new_text: z.string().nullable(),
    old_text: z.string().nullable(),
    term: z.string()
});

/**
 * ContextFigureChange
 */
export const zContextFigureChange = z.object({
    caption_changed: z.boolean(),
    change: zContextChange,
    image_changed: z.boolean(),
    new_caption: z.string().nullable(),
    new_page: z.int().nullable(),
    old_caption: z.string().nullable(),
    old_page: z.int().nullable(),
    owner: z.string()
});

/**
 * ContextRowChange
 */
export const zContextRowChange = z.object({
    change: zContextChange,
    new_cells: z.array(z.string()).nullable(),
    old_cells: z.array(z.string()).nullable()
});

/**
 * ContextStemChange
 */
export const zContextStemChange = z.object({
    change: zContextChange,
    new_text: z.string().nullable(),
    number: z.string(),
    old_text: z.string().nullable()
});

/**
 * ContextTableChange
 */
export const zContextTableChange = z.object({
    caption_changed: z.boolean(),
    change: zContextChange,
    new_caption: z.string().nullable(),
    new_page: z.int().nullable(),
    new_row_count: z.int(),
    old_caption: z.string().nullable(),
    old_page: z.int().nullable(),
    old_row_count: z.int(),
    owner: z.string(),
    rows: z.array(zContextRowChange)
});

/**
 * ClauseContextDiffOut
 */
export const zClauseContextDiffOut = z.object({
    compared: zContextCounts,
    definitions: z.array(zContextDefinitionChange),
    figures: z.array(zContextFigureChange),
    new_clause_uid: z.string(),
    old_clause_uid: z.string(),
    stems: z.array(zContextStemChange),
    tables: z.array(zContextTableChange)
});

/**
 * ControlFunctionLabel
 */
export const zControlFunctionLabel = z.object({
    mode: z.string(),
    title: z.string()
});

/**
 * CostCap
 */
export const zCostCap = z.object({
    amount: z.number().gt(0),
    currency: z.enum(['EUR', 'USD']),
    usd: z.number().gt(0),
    usd_per_eur: z.number().gt(0)
});

/**
 * CostLine
 */
export const zCostLine = z.object({
    task: z.string(),
    units: z.int().gte(0),
    usd: z.number().gte(0)
});

/**
 * CostEstimate
 */
export const zCostEstimate = z.object({
    basis: z.string().nullable(),
    breakdown: z.array(zCostLine),
    cost_usd: z.number().nullable(),
    discount_note: z.string().nullable(),
    duration_high_s: z.number().nullable(),
    duration_s: z.number().nullable(),
    high_usd: z.number().nullable(),
    low_usd: z.number().nullable(),
    method: z.enum(['comparable_run', 'model']).nullable(),
    translation_usd: z.number().nullable()
});

/**
 * AssessmentEstimate
 */
export const zAssessmentEstimate = z.object({
    ai_service_enabled: z.boolean(),
    capability_count: z.int().gte(0),
    estimate: zCostEstimate.nullable(),
    topics_total: z.int().gte(0),
    within_cap: z.boolean()
});

/**
 * CoverHints
 */
export const zCoverHints = z.object({
    published_on_hint: z.iso.date().nullable(),
    publisher_hint: z.string().nullable(),
    title: z.string().nullable(),
    version_label_hint: z.string().nullable()
});

/**
 * Coverage
 */
export const zCoverage = z.object({
    done: z.int().gte(0),
    total: z.int().gte(0)
});

/**
 * CriterionStatus
 */
export const zCriterionStatus = z.enum([
    'passed',
    'failed',
    'pending',
    'not_measured'
]);

/**
 * AcceptanceCriterion
 */
export const zAcceptanceCriterion = z.object({
    description: z.string(),
    detail: z.string(),
    key: z.string(),
    status: zCriterionStatus
});

/**
 * CurveFacets
 */
export const zCurveFacets = z.object({
    curve_type: z.record(z.string(), z.int()),
    digitized_by: z.record(z.string(), z.int()),
    region: z.record(z.string(), z.int()),
    source_kind: z.record(z.string(), z.int()),
    total: z.int()
});

/**
 * CurveRegion
 */
export const zCurveRegion = z.enum([
    'above',
    'below',
    'inside',
    'outside',
    'on'
]);

/**
 * CurveSource
 */
export const zCurveSource = z.enum([
    'table',
    'figure',
    'text'
]);

/**
 * CurveType
 */
export const zCurveType = z.enum([
    'frt_lvrt',
    'frt_hvrt',
    'voltage_time',
    'frequency_time',
    'pq_capability',
    'uq_capability',
    'p_f',
    'q_u',
    'k_factor',
    'ramp',
    'other'
]);

/**
 * CurveFamily
 */
export const zCurveFamily = z.object({
    curve_family_uid: z.string(),
    curve_type: zCurveType,
    family_parameter: z.string(),
    family_parameter_unit: z.string().nullable(),
    req_uid: z.string(),
    run_uid: z.string().nullable()
});

/**
 * DatasetAction
 */
export const zDatasetAction = z.enum([
    'registry.jurisdiction.created',
    'registry.jurisdiction.updated',
    'registry.jurisdiction.retired',
    'registry.authority.created',
    'registry.authority.updated',
    'registry.authority.retired',
    'registry.family.created',
    'registry.family.updated',
    'registry.family.retired',
    'registry.family.sections_replaced',
    'registry.version.created',
    'registry.version.updated',
    'registry.version.retired',
    'registry.seeded',
    'source.uploaded',
    'source.deduplicated',
    'source.profiled',
    'upload.refused',
    'job.requested',
    'job.cancelled',
    'job.auto_resumed',
    'job.finished',
    'assessment.created',
    'assessment.approved',
    'assessment.reopened',
    'assessment.deleted',
    'topic.approved',
    'topic.changed',
    'topic.reset',
    'document.ready',
    'document.promoted',
    'document.status_changed',
    'document.deleted',
    'run.rolled_back',
    'capability_document.uploaded',
    'capability_document.extracted',
    'capability_document.deleted',
    'assessment.finished',
    'assessment.failed',
    'settings.updated'
]);

/**
 * DatasetsOverview
 */
export const zDatasetsOverview = z.object({
    active_jobs: z.int().gte(0),
    building: z.int().gte(0),
    families: z.int().gte(0),
    launcher: z.enum([
        'local',
        'worker',
        'aca',
        'none'
    ]),
    month_to_date_usd: z.number().gte(0),
    not_built: z.int().gte(0),
    queued_jobs: z.int().gte(0),
    ready: z.int().gte(0),
    run_cost_cap: zCostCap,
    stale: z.int().gte(0),
    versions: z.int().gte(0),
    workers_online: z.int().nullable()
});

/**
 * Definition
 */
export const zDefinition = z.object({
    clause_uid: z.string().nullable(),
    definition_uid: z.string(),
    doc_uid: z.string(),
    is_abbreviation: z.boolean().default(false),
    language: z.string(),
    page: z.int(),
    run_uid: z.string().nullable(),
    term: z.string(),
    term_en: z.string().nullable(),
    text: z.string(),
    text_en: z.string().nullable(),
    translation_sha256: z.string().nullable()
});

/**
 * DefinitionRef
 */
export const zDefinitionRef = z.object({
    definition_uid: z.string(),
    term: z.string(),
    text: z.string()
});

/**
 * DefinitionRow
 */
export const zDefinitionRow = z.object({
    clause_uid: z.string().nullable(),
    definition_uid: z.string(),
    is_abbreviation: z.boolean(),
    page: z.int(),
    term: z.string(),
    text: z.string(),
    usage_count: z.int()
});

/**
 * DeleteVersionJobRequest
 */
export const zDeleteVersionJobRequest = z.object({
    doc_uid: z.string(),
    force: z.boolean().optional().default(false),
    kind: z.literal('delete_version'),
    retire_registry_version: z.boolean().optional().default(false)
});

/**
 * DependsOn
 */
export const zDependsOn = z.object({
    clause_uid: z.string().nullable(),
    number: z.string()
});

/**
 * DiffCounts
 */
export const zDiffCounts = z.object({
    changed: z.int(),
    changed_context: z.int(),
    changed_text: z.int(),
    new: z.int(),
    removed: z.int(),
    renumbered: z.int(),
    unchanged: z.int()
});

/**
 * DigitizedBy
 */
export const zDigitizedBy = z.enum([
    'llm_text',
    'llm_vision',
    'manual'
]);

/**
 * DocType
 */
export const zDocType = z.enum([
    'eu_regulation',
    'national_code',
    'technical_rule',
    'operator_requirement',
    'guidance',
    'standard',
    'other'
]);

/**
 * DocumentStatus
 */
export const zDocumentStatus = z.enum([
    'draft',
    'published',
    'in_force',
    'superseded',
    'archived'
]);

/**
 * AssessmentGridCode
 */
export const zAssessmentGridCode = z.object({
    doc_uid: z.string(),
    family_title: z.string(),
    jurisdiction_id: z.string().nullable(),
    status: zDocumentStatus,
    version_label: z.string()
});

/**
 * ClauseVersion
 */
export const zClauseVersion = z.object({
    change: zClauseChange.nullable(),
    clause_uid: z.string(),
    current: z.boolean(),
    doc_uid: z.string(),
    number: z.string(),
    status: zDocumentStatus,
    version_label: z.string()
});

/**
 * ClauseHistory
 */
export const zClauseHistory = z.object({
    added_in: z.string().nullable(),
    dropped_in: z.string().nullable(),
    versions: z.array(zClauseVersion)
});

/**
 * EffectiveSection
 */
export const zEffectiveSection = z.object({
    code: z.string(),
    extract: z.string(),
    load_clauses: z.boolean(),
    normative: z.boolean(),
    priority: z.enum([
        'P1',
        'P2',
        'P3'
    ]),
    source: z.enum(['registry', 'profile']),
    title: z.string()
});

/**
 * EffectiveSetting
 */
export const zEffectiveSetting = z.object({
    file: z.string(),
    from_admin: z.boolean().optional().default(false),
    from_environment: z.boolean(),
    name: z.string(),
    value: z.string()
});

/**
 * EmbeddingsOut
 */
export const zEmbeddingsOut = z.object({
    dimensions: z.int(),
    model: z.string(),
    price_usd_per_mtok: z.number(),
    provider: z.string(),
    rerank_model: z.string().nullable(),
    similarity: z.string()
});

/**
 * EntryMeta
 */
export const zEntryMeta = z.object({
    created_at: z.iso.datetime({ offset: true }),
    created_by: zActor,
    entry_key: z.string(),
    etag: z.string(),
    origin: z.enum([
        'seed',
        'ui',
        'cli'
    ]),
    revision: z.int().gte(0),
    status: z.enum(['active', 'retired']),
    updated_at: z.iso.datetime({ offset: true }),
    updated_by: zActor
});

/**
 * EntryRevision
 */
export const zEntryRevision = z.object({
    actor: zActor,
    at: z.iso.datetime({ offset: true }),
    body: z.record(z.string(), z.unknown()),
    entry_key: z.string(),
    event_id: z.string(),
    revision: z.int(),
    status: z.string()
});

/**
 * EntryUsage
 */
export const zEntryUsage = z.object({
    documents: z.int().gte(0),
    families: z.int().gte(0),
    versions: z.int().gte(0)
});

/**
 * AuthorityEntry
 */
export const zAuthorityEntry = z.object({
    authority: zAuthority,
    meta: zEntryMeta,
    usage: zEntryUsage
});

/**
 * EstimateInputs
 */
export const zEstimateInputs = z.object({
    chars_by_priority: z.record(z.string(), z.int()),
    clauses_by_priority: z.record(z.string(), z.int()),
    compared_with: z.string().nullable(),
    figures: z.int().gte(0),
    sections: z.int().gte(0),
    translation_chars: z.int().gte(0),
    unchanged_in_scope: z.int().gte(0)
});

/**
 * EvaluationJobRequest
 */
export const zEvaluationJobRequest = z.object({
    doc_uid: z.string(),
    family_id: z.string(),
    k: z.int().gte(1).lte(50).optional().default(10),
    kind: z.literal('evaluation')
});

/**
 * EvaluationReportRef
 */
export const zEvaluationReportRef = z.object({
    doc_uid: z.string(),
    generated_at: z.iso.datetime({ offset: true }).nullable(),
    kind: z.enum(['recall', 'search']),
    meets_target: z.boolean(),
    path: z.string(),
    rate: z.number().nullable(),
    target: z.number()
});

/**
 * ExtractedValue
 */
export const zExtractedValue = z.object({
    field: z.enum([
        'value',
        'value_min',
        'value_max',
        'time_limit_s',
        'band_lower',
        'band_upper'
    ]),
    key: z.string(),
    label: z.string(),
    number: z.number(),
    unit: z.string().nullable()
});

/**
 * ExtractionMethod
 */
export const zExtractionMethod = z.enum([
    'single_pass',
    'targeted_recheck',
    'reused',
    'manual'
]);

/**
 * ExtractionStatus
 */
export const zExtractionStatus = z.enum([
    'not_in_scope',
    'pending',
    'extracted',
    'reused',
    'informative',
    'failed'
]);

/**
 * Clause
 */
export const zClause = z.object({
    bbox: z.array(z.number()),
    clause_uid: z.string(),
    content_hash: z.string(),
    context_hash: z.string().nullable(),
    doc_uid: z.string(),
    extraction_status: zExtractionStatus.default('not_in_scope'),
    family_id: z.string(),
    in_scope: z.boolean().default(false),
    informative_reason: z.string().nullable(),
    kind: zClauseKind,
    language: z.string(),
    level: z.int(),
    normative: z.boolean().nullable(),
    number: z.string(),
    order: z.int(),
    page_end: z.int(),
    page_start: z.int(),
    parent_uid: z.string().nullable(),
    priority: z.string().nullable(),
    quality_flags: z.array(z.string()),
    run_uid: z.string().nullable(),
    section_code: z.string(),
    section_stamp: z.string().nullable(),
    summary: z.string().nullable(),
    text: z.string().default(''),
    text_en: z.string().nullable(),
    title: z.string().nullable(),
    title_en: z.string().nullable(),
    translation_model: z.string().nullable(),
    translation_sha256: z.string().nullable(),
    unresolved_references: z.array(z.string())
});

/**
 * ClauseSummary
 */
export const zClauseSummary = z.object({
    clause_uid: z.string(),
    extraction_status: zExtractionStatus,
    kind: zClauseKind,
    level: z.int(),
    normative: z.boolean().nullable(),
    number: z.string(),
    page_end: z.int(),
    page_start: z.int(),
    priority: z.string().nullable(),
    quality_flags: z.array(z.string()),
    requirement_count: z.int(),
    section_code: z.string(),
    title: z.string().nullable(),
    unresolved_references: z.array(z.string())
});

/**
 * CompletenessGap
 */
export const zCompletenessGap = z.object({
    clause_uid: z.string(),
    extraction_status: zExtractionStatus,
    normative: z.boolean().nullable(),
    number: z.string(),
    page_start: z.int(),
    priority: z.string().nullable(),
    reason: z.string(),
    section_code: z.string(),
    title: z.string().nullable()
});

/**
 * FamilyRef
 */
export const zFamilyRef = z.object({
    curve_family_uid: z.string(),
    family_parameter: z.string(),
    family_parameter_unit: z.string().nullable(),
    value: z.number().nullable()
});

/**
 * FeedbackErrorSeen
 */
export const zFeedbackErrorSeen = z.object({
    at: z.iso.datetime({ offset: true }),
    message: z.string().max(1000),
    source: z.string().max(500).nullable()
});

/**
 * FeedbackKind
 */
export const zFeedbackKind = z.enum([
    'bug',
    'friction',
    'feature_request',
    'question',
    'praise'
]);

/**
 * FeedbackProblemSeen
 */
export const zFeedbackProblemSeen = z.object({
    at: z.iso.datetime({ offset: true }),
    code: z.string().max(100),
    method: z.string().max(16),
    path: z.string().max(500),
    status: z.int().gte(0).lte(599),
    trace_id: z.string().max(64).nullable()
});

/**
 * FeedbackContext
 */
export const zFeedbackContext = z.object({
    browser: z.string().max(500),
    errors: z.array(zFeedbackErrorSeen).max(20),
    language: z.string().max(40).nullable(),
    page: z.string().max(2000),
    problems: z.array(zFeedbackProblemSeen).max(20),
    viewport: z.string().max(40),
    web_release: z.string().max(100).nullable()
});

/**
 * FeedbackReceipt
 */
export const zFeedbackReceipt = z.object({
    received_at: z.iso.datetime({ offset: true }),
    report_id: z.string(),
    screenshot: z.boolean()
});

/**
 * FeedbackScreenshot
 */
export const zFeedbackScreenshot = z.object({
    height: z.int(),
    media_type: z.enum(['image/png', 'image/jpeg']),
    size_bytes: z.int(),
    whole_screen: z.boolean(),
    width: z.int()
});

/**
 * FeedbackScreenshotIn
 */
export const zFeedbackScreenshotIn = z.object({
    data: z.string().min(4).max(5592408),
    whole_screen: z.boolean().optional().default(false)
});

/**
 * FeedbackCreate
 */
export const zFeedbackCreate = z.object({
    context: zFeedbackContext,
    description: z.string().max(20000).optional().default(''),
    kind: zFeedbackKind,
    screenshot: zFeedbackScreenshotIn.nullish(),
    title: z.string().min(3).max(200)
});

/**
 * FeedbackStatus
 */
export const zFeedbackStatus = z.enum([
    'new',
    'planned',
    'done',
    'wont_do'
]);

/**
 * FeedbackReport
 */
export const zFeedbackReport = z.object({
    author: zActorRef,
    created_at: z.iso.datetime({ offset: true }),
    has_screenshot: z.boolean(),
    kind: zFeedbackKind,
    number: z.int(),
    page: z.string(),
    report_id: z.string(),
    status: zFeedbackStatus,
    title: z.string(),
    updated_at: z.iso.datetime({ offset: true }).nullable()
});

/**
 * FeedbackReportDetail
 */
export const zFeedbackReportDetail = z.object({
    api_release: z.string().nullable(),
    author: zActorRef,
    context: zFeedbackContext,
    created_at: z.iso.datetime({ offset: true }),
    description: z.string(),
    has_screenshot: z.boolean(),
    kind: zFeedbackKind,
    note: z.string(),
    number: z.int(),
    page: z.string(),
    report_id: z.string(),
    screenshot: zFeedbackScreenshot.nullable(),
    status: zFeedbackStatus,
    title: z.string(),
    updated_at: z.iso.datetime({ offset: true }).nullable(),
    updated_by: zActorRef.nullable()
});

/**
 * FeedbackSummary
 */
export const zFeedbackSummary = z.object({
    new: z.int()
});

/**
 * FeedbackUpdate
 */
export const zFeedbackUpdate = z.object({
    note: z.string().max(20000).nullish(),
    status: zFeedbackStatus.nullish()
});

/**
 * FieldChange
 */
export const zFieldChange = z.object({
    after: z.string().nullable(),
    before: z.string().nullable(),
    field: z.string()
});

/**
 * FigureMark
 */
export const zFigureMark = z.object({
    bbox: z.array(z.number()),
    caption: z.string().nullable(),
    curve_count: z.int(),
    figure_uid: z.string(),
    key: z.string(),
    point_count: z.int()
});

/**
 * FigureRow
 */
export const zFigureRow = z.object({
    bbox: z.array(z.number()),
    caption: z.string().nullable(),
    clause_number: z.string().nullable(),
    clause_uid: z.string().nullable(),
    curve_count: z.int(),
    figure_uid: z.string(),
    key: z.string(),
    kind: z.string(),
    page: z.int()
});

/**
 * FixedSetting
 */
export const zFixedSetting = z.object({
    name: z.string(),
    reason: z.string(),
    value: z.string()
});

/**
 * ConfigurationReport
 */
export const zConfigurationReport = z.object({
    connections: z.record(z.string(), z.string().nullable()),
    embedding_price_usd_per_mtok: z.number(),
    fixed_settings: z.array(zFixedSetting),
    prices_usd_per_mtok: z.record(z.string(), z.record(z.string(), z.number())),
    settings: z.array(zEffectiveSetting)
});

/**
 * FurniturePattern
 */
export const zFurniturePattern = z.object({
    page_share: z.number().gte(0).lte(1),
    template: z.string(),
    zone: z.enum(['top', 'bottom'])
});

/**
 * GlossarySummary
 */
export const zGlossarySummary = z.object({
    entries: z.int().gte(0),
    sample: z.array(z.string()),
    shape: z.string()
});

/**
 * GridConnection
 */
export const zGridConnection = z.enum(['new', 'existing']);

/**
 * AssessmentCreate
 */
export const zAssessmentCreate = z.object({
    capability_doc_uid: z.string().regex(/^[0-9a-f]{64}$/),
    doc_uid: z.string(),
    grid_connection: zGridConnection.nullish(),
    name: z.string().min(1).max(200).nullish(),
    plant_type: z.enum([
        'A',
        'B',
        'C',
        'D'
    ]).nullish(),
    product_types: z.array(z.string().min(1)).optional()
});

/**
 * GroupCount
 */
export const zGroupCount = z.object({
    count: z.int(),
    group_id: z.string(),
    name: z.string(),
    order: z.int()
});

/**
 * GroupOut
 */
export const zGroupOut = z.object({
    approved_count: z.int(),
    deprecated_count: z.int(),
    description: z.string(),
    group_id: z.string(),
    name: z.string(),
    order: z.int(),
    requirement_count: z.int()
});

/**
 * Health
 */
export const zHealth = z.object({
    status: z.literal('ok').default('ok')
});

/**
 * ImpactVersion
 */
export const zImpactVersion = z.object({
    doc_uid: z.string(),
    status: zDocumentStatus,
    valid_from: z.iso.date().nullable(),
    version_label: z.string()
});

/**
 * IndexStatus
 */
export const zIndexStatus = z.object({
    labels: z.array(z.string()),
    name: z.string(),
    population_percent: z.number(),
    properties: z.array(z.string()),
    state: z.string(),
    type: z.string()
});

/**
 * IngestDryRunJobRequest
 */
export const zIngestDryRunJobRequest = z.object({
    family_id: z.string(),
    kind: z.literal('ingest_dry_run'),
    version_label: z.string()
});

/**
 * IngestJobRequest
 */
export const zIngestJobRequest = z.object({
    accept_config_change: z.boolean().optional().default(false),
    allow_unsupported_languages: z.boolean().optional().default(false),
    clauses: z.array(z.string()).nullish(),
    family_id: z.string(),
    flagged_only: z.boolean().optional().default(false),
    kind: z.literal('ingest'),
    max_clauses: z.int().gte(1).nullish(),
    max_cost_usd: z.number().gt(0).nullish(),
    mode: z.enum(['sync', 'batch']).optional().default('batch'),
    priorities: z.array(z.enum([
        'P1',
        'P2',
        'P3'
    ])).optional(),
    version_label: z.string()
});

/**
 * IngestResumeJobRequest
 */
export const zIngestResumeJobRequest = z.object({
    accept_config_change: z.boolean().optional().default(false),
    allow_unsupported_languages: z.boolean().optional().default(false),
    kind: z.literal('ingest_resume'),
    max_cost_usd: z.number().gt(0).nullish(),
    refresh_registry: z.boolean().optional().default(false),
    run_uid: z.string()
});

/**
 * IntegrityCheck
 */
export const zIntegrityCheck = z.object({
    blocking: z.boolean(),
    count: z.int(),
    key: z.string()
});

/**
 * IntegrityReport
 */
export const zIntegrityReport = z.object({
    checks: z.array(zIntegrityCheck)
});

/**
 * Interpolation
 */
export const zInterpolation = z.enum([
    'linear',
    'step',
    'log_x'
]);

/**
 * InvitationAcceptance
 */
export const zInvitationAcceptance = z.object({
    name: z.string().min(1).max(120),
    password: z.string().min(1).max(1024),
    token: z.string().min(1).max(1024)
});

/**
 * InvitationLookupRequest
 */
export const zInvitationLookupRequest = z.object({
    token: z.string().min(1).max(1024)
});

/**
 * InvitationStatus
 */
export const zInvitationStatus = z.enum([
    'pending',
    'accepted',
    'revoked',
    'expired'
]);

/**
 * JobError
 */
export const zJobError = z.object({
    message: z.string(),
    type: z.string()
});

/**
 * JobExecution
 */
export const zJobExecution = z.object({
    host: z.string().nullable(),
    launcher: z.enum([
        'local',
        'worker',
        'aca',
        'none'
    ]),
    logs_hint: z.string().nullable(),
    name: z.string().nullable()
});

/**
 * JobKind
 */
export const zJobKind = z.enum([
    'ingest',
    'ingest_resume',
    'ingest_dry_run',
    'rollback',
    'delete_version',
    'consolidate_parameters',
    'source_watch',
    'evaluation',
    'source_profile',
    'capability_extract',
    'assessment_run'
]);

/**
 * JobProgress
 */
export const zJobProgress = z.object({
    cost_usd: z.number().gte(0).nullable(),
    detail: z.string().nullable(),
    eta_s: z.number().gte(0).nullable(),
    fraction: z.number().gte(0).lte(1).nullable(),
    items_done: z.int().gte(0).nullable(),
    items_total: z.int().gte(0).nullable(),
    phase: z.enum([
        'queued',
        'starting',
        'step',
        'waiting_for_batch',
        'translating',
        'applying',
        'cancelling'
    ]).nullable(),
    step: z.string().nullable(),
    steps_done: z.int().nullable(),
    steps_total: z.int().nullable(),
    sub_step: z.string().nullable()
});

/**
 * JobProgressEvent
 */
export const zJobProgressEvent = z.object({
    event: z.literal('progress').default('progress'),
    progress: zJobProgress
});

/**
 * JobQueueInfo
 */
export const zJobQueueInfo = z.object({
    position: z.int().gte(1),
    reason: z.string()
});

/**
 * JobRequester
 */
export const zJobRequester = z.object({
    name: z.string(),
    oid: z.string()
});

/**
 * JobStatus
 */
export const zJobStatus = z.enum([
    'queued',
    'running',
    'succeeded',
    'failed',
    'cancelled',
    'interrupted'
]);

/**
 * JobBrief
 */
export const zJobBrief = z.object({
    job_id: z.string(),
    kind: zJobKind,
    progress: zJobProgress.nullable(),
    queue: zJobQueueInfo.nullable(),
    status: zJobStatus
});

/**
 * JsonPatchOp
 */
export const zJsonPatchOp = z.object({
    from: z.string().nullable(),
    op: z.enum([
        'add',
        'remove',
        'replace',
        'move',
        'copy',
        'test'
    ]),
    path: z.string(),
    value: z.unknown().nullable()
});

/**
 * DatasetEventOut
 */
export const zDatasetEventOut = z.object({
    action: zDatasetAction,
    actor: zActor,
    at: z.iso.datetime({ offset: true }),
    context: z.record(z.string(), z.unknown()),
    entity_id: z.string().nullable(),
    entity_kind: z.string().nullable(),
    event_id: z.string(),
    hash: z.string(),
    patch: z.array(zJsonPatchOp).nullable(),
    prev_hash: z.string(),
    revision: z.int().nullable(),
    seq: z.int().gte(1),
    summary: z.string()
});

export const zJsonValue = z.unknown();

/**
 * JurisdictionKind
 */
export const zJurisdictionKind = z.enum([
    'union',
    'country',
    'region',
    'synchronous_area',
    'market'
]);

/**
 * Jurisdiction
 */
export const zJurisdiction = z.object({
    iso_code: z.string().nullable(),
    jurisdiction_id: z.string(),
    kind: zJurisdictionKind,
    name: z.string(),
    part_of: z.array(z.string())
});

/**
 * JurisdictionEntry
 */
export const zJurisdictionEntry = z.object({
    jurisdiction: zJurisdiction,
    meta: zEntryMeta,
    usage: zEntryUsage
});

/**
 * JurisdictionIn
 */
export const zJurisdictionIn = z.object({
    iso_code: z.string().max(8).nullish(),
    jurisdiction_id: z.string().max(32).regex(/^[A-Z][A-Z0-9]*(-[A-Z0-9]+)*$/),
    kind: zJurisdictionKind,
    name: z.string().min(1).max(300),
    part_of: z.array(z.string().max(32).regex(/^[A-Z][A-Z0-9]*(-[A-Z0-9]+)*$/)).optional()
});

/**
 * JurisdictionOut
 */
export const zJurisdictionOut = z.object({
    current_doc_uid: z.string().nullable(),
    current_family_title: z.string().nullable(),
    current_version_label: z.string().nullable(),
    document_count: z.int(),
    iso_code: z.string().nullable(),
    jurisdiction_id: z.string(),
    kind: zJurisdictionKind,
    name: z.string(),
    part_of: z.array(z.string()),
    selectable: z.boolean()
});

/**
 * JurisdictionPatch
 */
export const zJurisdictionPatch = z.object({
    iso_code: z.string().max(8).nullish(),
    kind: zJurisdictionKind.nullish(),
    name: z.string().min(1).max(300).nullish(),
    part_of: z.array(z.string().max(32).regex(/^[A-Z][A-Z0-9]*(-[A-Z0-9]+)*$/)).nullish()
});

/**
 * LanguageDetection
 */
export const zLanguageDetection = z.object({
    bilingual_layout: z.boolean(),
    confidence: z.number().gte(0).lte(1),
    number_locale: z.enum(['decimal_comma', 'decimal_point']).nullable(),
    primary: z.string(),
    shares: z.record(z.string(), z.number())
});

/**
 * LanguageSummary
 */
export const zLanguageSummary = z.object({
    bilingual_layout: z.boolean(),
    confidence: z.number().gte(0).lte(1),
    number_locale: z.string().nullable(),
    obligation_scan: z.enum(['native', 'pivot']),
    primary: z.string(),
    translation_required: z.boolean()
});

/**
 * LanguageSupport
 */
export const zLanguageSupport = z.object({
    code: z.string(),
    fulltext: z.string(),
    name: z.string(),
    obligation_scan: z.enum(['native', 'pivot']),
    translation: z.enum(['not_needed', 'machine'])
});

/**
 * LayoutProfileRef
 */
export const zLayoutProfileRef = z.object({
    profile_sha256: z.string().regex(/^[0-9a-f]{64}$/).nullable(),
    profiler_version: z.string().min(1).max(16),
    sha256: z.string().regex(/^[0-9a-f]{64}$/)
});

/**
 * LevelVerdictStatus
 */
export const zLevelVerdictStatus = z.enum([
    'compliant',
    'compliance_expected',
    'assessed_at_other_level',
    'wind_farm_study',
    'developer_scope',
    'to_be_agreed',
    'open',
    'not_compliant',
    'not_available',
    'not_applicable',
    'no_requirement'
]);

/**
 * LicenceClass
 */
export const zLicenceClass = z.enum([
    'public',
    'licensed',
    'confidential'
]);

/**
 * DocumentOut
 */
export const zDocumentOut = z.object({
    applies_in: z.array(z.string()),
    build_projection_sha256: z.string().nullable(),
    build_state: zBuildState.default('ready'),
    content_revision: z.int().gte(0).default(0),
    doc_uid: z.string(),
    family_id: z.string(),
    family_title: z.string().nullable(),
    file_name: z.string().nullable(),
    file_sha256: z.string(),
    implements: z.array(z.string()),
    in_effect: z.boolean(),
    issued_by: z.string().nullable(),
    jurisdictions: z.array(z.string()),
    language: z.string(),
    languages: z.array(z.string()),
    licence_class: zLicenceClass,
    page_count: z.int().nullable(),
    profile_sha256: z.string().nullable(),
    published_on: z.iso.date().nullable(),
    quality_flags: z.array(z.string()),
    quality_score: z.number().gte(0).lte(100).nullable(),
    ready_at: z.iso.datetime({ offset: true }).nullable(),
    ready_run_uid: z.string().nullable(),
    registry_revision: z.int().nullable(),
    retrieved_on: z.iso.date().nullable(),
    run_uid: z.string().nullable(),
    schema_version: z.string().default('1.8'),
    source_url: z.string().nullable(),
    status: zDocumentStatus,
    title: z.string(),
    updating_run: z.string().nullable(),
    valid_from: z.iso.date().nullable(),
    valid_to: z.iso.date().nullable(),
    version_label: z.string()
});

/**
 * DocumentRef
 */
export const zDocumentRef = z.object({
    doc_uid: z.string(),
    family_id: z.string(),
    language: z.string(),
    licence_class: zLicenceClass,
    status: zDocumentStatus,
    title: z.string(),
    valid_from: z.iso.date().nullable(),
    valid_to: z.iso.date().nullable(),
    version_label: z.string()
});

/**
 * DocumentRow
 */
export const zDocumentRow = z.object({
    applies_in: z.array(z.string()),
    build_projection_sha256: z.string().nullable(),
    build_state: zBuildState.default('ready'),
    clause_count: z.int(),
    content_revision: z.int().gte(0).default(0),
    doc_uid: z.string(),
    family_id: z.string(),
    family_title: z.string().nullable(),
    file_name: z.string().nullable(),
    file_sha256: z.string(),
    implements: z.array(z.string()),
    in_effect: z.boolean(),
    issued_by: z.string().nullable(),
    jurisdictions: z.array(z.string()),
    language: z.string(),
    languages: z.array(z.string()),
    licence_class: zLicenceClass,
    page_count: z.int().nullable(),
    profile_sha256: z.string().nullable(),
    published_on: z.iso.date().nullable(),
    quality_flags: z.array(z.string()),
    quality_score: z.number().gte(0).lte(100).nullable(),
    ready_at: z.iso.datetime({ offset: true }).nullable(),
    ready_run_uid: z.string().nullable(),
    registry_revision: z.int().nullable(),
    requirement_count: z.int(),
    retrieved_on: z.iso.date().nullable(),
    run_uid: z.string().nullable(),
    schema_version: z.string().default('1.8'),
    source_url: z.string().nullable(),
    status: zDocumentStatus,
    title: z.string(),
    updating_run: z.string().nullable(),
    valid_from: z.iso.date().nullable(),
    valid_to: z.iso.date().nullable(),
    version_label: z.string()
});

/**
 * LlmTaskOut
 */
export const zLlmTaskOut = z.object({
    effort: z.string().nullable(),
    max_tokens: z.int(),
    model: z.string(),
    prompt_version: z.string(),
    task: z.string(),
    thinking: z.string()
});

/**
 * LocateText
 */
export const zLocateText = z.object({
    key: z.string().min(1).max(64),
    text: z.string().min(1).max(2000)
});

/**
 * LocateRequest
 */
export const zLocateRequest = z.object({
    texts: z.array(zLocateText).min(1).max(20)
});

/**
 * Logic
 */
export const zLogic = z.enum(['all', 'any']);

/**
 * ApplicabilityOut
 */
export const zApplicabilityOut = z.object({
    applicability_uid: z.string(),
    applies_to_all: z.boolean(),
    leaves_deduped: z.array(zConditionLeaf),
    logic: zLogic,
    summary: z.string().nullable(),
    tree: z.array(zConditionNode)
});

/**
 * MarketCurve
 */
export const zMarketCurve = z.object({
    curve_type: zCurveType,
    curve_uid: z.string(),
    family_parameter_value: z.number().nullable(),
    interpolation: zInterpolation,
    name: z.string(),
    region: zCurveRegion,
    source_kind: zCurveSource,
    verified_by: z.string().nullable(),
    x: z.array(z.number()),
    x_base: zBaseQuantity,
    x_unit: z.string(),
    y: z.array(z.number()),
    y_base: zBaseQuantity,
    y_unit: z.string()
});

/**
 * Modality
 */
export const zModality = z.enum([
    'shall',
    'shall_not',
    'should',
    'should_not',
    'may'
]);

/**
 * CurveRequirement
 */
export const zCurveRequirement = z.object({
    acceptance: z.string().nullable(),
    modality: zModality,
    req_uid: z.string(),
    statement: z.string()
});

/**
 * OmniboxClause
 */
export const zOmniboxClause = z.object({
    clause_uid: z.string(),
    number: z.string(),
    page_start: z.int(),
    title: z.string().nullable()
});

/**
 * OmniboxDefinition
 */
export const zOmniboxDefinition = z.object({
    definition_uid: z.string(),
    term: z.string()
});

/**
 * OmniboxParameter
 */
export const zOmniboxParameter = z.object({
    group_id: z.string(),
    name: z.string(),
    parameter_id: z.string()
});

/**
 * OpenParameterOwner
 */
export const zOpenParameterOwner = z.enum([
    'relevant_system_operator',
    'tso',
    'dso',
    'regulator',
    'bilateral_agreement',
    'member_state',
    'other'
]);

/**
 * MarketOpenParameter
 */
export const zMarketOpenParameter = z.object({
    decided_by: zOpenParameterOwner,
    description: z.string(),
    name: z.string(),
    open_parameter_uid: z.string()
});

/**
 * OpenParameter
 */
export const zOpenParameter = z.object({
    decided_by: zOpenParameterOwner,
    description: z.string(),
    name: z.string(),
    open_parameter_uid: z.string(),
    req_uid: z.string(),
    run_uid: z.string().nullable(),
    source_text: z.string().nullable(),
    typical_range_text: z.string().nullable()
});

/**
 * OperationJob
 */
export const zOperationJob = z.object({
    attempt: z.int().gte(0).default(0),
    auto_resumed_by: z.string().nullable(),
    cancel_requested_at: z.iso.datetime({ offset: true }).nullable(),
    dispatched_at: z.iso.datetime({ offset: true }).nullable(),
    doc_uid: z.string().nullable(),
    error: zJobError.nullable(),
    estimate: zCostEstimate.nullable(),
    execution: zJobExecution.nullable(),
    finished_at: z.iso.datetime({ offset: true }).nullable(),
    heartbeat_at: z.iso.datetime({ offset: true }).nullable(),
    job_id: z.string(),
    kind: zJobKind,
    max_cost_usd: z.number().nullable(),
    params: z.record(z.string(), z.unknown()),
    pid: z.int().nullable(),
    pool: z.enum([
        'paid',
        'maintenance',
        'light'
    ]).nullable(),
    progress: zJobProgress.nullable(),
    queue: zJobQueueInfo.nullable(),
    registry_revision: z.int().nullable(),
    requested_at: z.iso.datetime({ offset: true }),
    requested_by: zJobRequester,
    result: z.record(z.string(), z.unknown()).nullable(),
    run_uid: z.string().nullable(),
    started_at: z.iso.datetime({ offset: true }).nullable(),
    status: zJobStatus
});

/**
 * JobDoneEvent
 */
export const zJobDoneEvent = z.object({
    event: z.literal('done').default('done'),
    job: zOperationJob
});

/**
 * JobStatusEvent
 */
export const zJobStatusEvent = z.object({
    event: z.literal('status').default('status'),
    job: zOperationJob
});

/**
 * JobEvent
 */
export const zJobEvent = z.discriminatedUnion('event', [
    zJobStatusEvent,
    zJobProgressEvent,
    zJobDoneEvent
]);

/**
 * PageClause
 */
export const zPageClause = z.object({
    bbox: z.array(z.number()),
    clause_uid: z.string(),
    kind: zClauseKind,
    number: z.string(),
    page_end: z.int(),
    page_start: z.int(),
    title: z.string().nullable()
});

/**
 * Page[AssistantThread]
 */
export const zPageAssistantThread = z.object({
    items: z.array(zAssistantThread),
    limit: z.int(),
    next_cursor: z.string().nullable(),
    total: z.int()
});

/**
 * Page[AuthEvent]
 */
export const zPageAuthEvent = z.object({
    items: z.array(zAuthEvent),
    limit: z.int(),
    next_cursor: z.string().nullable(),
    total: z.int()
});

/**
 * Page[AuthorityEntry]
 */
export const zPageAuthorityEntry = z.object({
    items: z.array(zAuthorityEntry),
    limit: z.int(),
    next_cursor: z.string().nullable(),
    total: z.int()
});

/**
 * Page[ClauseSummary]
 */
export const zPageClauseSummary = z.object({
    items: z.array(zClauseSummary),
    limit: z.int(),
    next_cursor: z.string().nullable(),
    total: z.int()
});

/**
 * Page[DatasetEventOut]
 */
export const zPageDatasetEventOut = z.object({
    items: z.array(zDatasetEventOut),
    limit: z.int(),
    next_cursor: z.string().nullable(),
    total: z.int()
});

/**
 * Page[DefinitionRow]
 */
export const zPageDefinitionRow = z.object({
    items: z.array(zDefinitionRow),
    limit: z.int(),
    next_cursor: z.string().nullable(),
    total: z.int()
});

/**
 * Page[EntryRevision]
 */
export const zPageEntryRevision = z.object({
    items: z.array(zEntryRevision),
    limit: z.int(),
    next_cursor: z.string().nullable(),
    total: z.int()
});

/**
 * Page[FeedbackReport]
 */
export const zPageFeedbackReport = z.object({
    items: z.array(zFeedbackReport),
    limit: z.int(),
    next_cursor: z.string().nullable(),
    total: z.int()
});

/**
 * Page[FigureRow]
 */
export const zPageFigureRow = z.object({
    items: z.array(zFigureRow),
    limit: z.int(),
    next_cursor: z.string().nullable(),
    total: z.int()
});

/**
 * Page[JurisdictionEntry]
 */
export const zPageJurisdictionEntry = z.object({
    items: z.array(zJurisdictionEntry),
    limit: z.int(),
    next_cursor: z.string().nullable(),
    total: z.int()
});

/**
 * ParameterGroup
 */
export const zParameterGroup = z.object({
    description: z.string(),
    group_id: z.string(),
    name: z.string(),
    order: z.int().default(0)
});

/**
 * ParameterMappingMethod
 */
export const zParameterMappingMethod = z.enum([
    'extraction',
    'vector_llm',
    'proposed',
    'reused',
    'previous_version',
    'manual'
]);

/**
 * ParameterSort
 */
export const zParameterSort = z.enum([
    'requirements',
    'id',
    'name'
]);

/**
 * ParameterStatus
 */
export const zParameterStatus = z.enum([
    'approved',
    'proposed',
    'deprecated'
]);

/**
 * PasswordChange
 */
export const zPasswordChange = z.object({
    current_password: z.string().min(1).max(1024),
    new_password: z.string().min(1).max(1024)
});

/**
 * PasswordPolicy
 */
export const zPasswordPolicy = z.object({
    breached_password_check: z.boolean(),
    max_length: z.int(),
    min_length: z.int()
});

/**
 * PasswordResetConfirmation
 */
export const zPasswordResetConfirmation = z.object({
    new_password: z.string().min(1).max(1024),
    token: z.string().min(1).max(1024)
});

/**
 * PasswordResetLookupRequest
 */
export const zPasswordResetLookupRequest = z.object({
    token: z.string().min(1).max(1024)
});

/**
 * PasswordResetPreview
 */
export const zPasswordResetPreview = z.object({
    email: z.string(),
    expires_at: z.iso.datetime({ offset: true })
});

/**
 * PasswordResetRequest
 */
export const zPasswordResetRequest = z.object({
    email: z.email()
});

/**
 * PasswordResetSent
 */
export const zPasswordResetSent = z.object({
    email_sent: z.boolean(),
    reset_url: z.string().nullable()
});

/**
 * PdfLink
 */
export const zPdfLink = z.object({
    text: z.string(),
    url: z.string()
});

/**
 * PlantTypeThreshold
 */
export const zPlantTypeThreshold = z.object({
    doc_uid: z.string(),
    jurisdiction_id: z.string(),
    max_capacity_mw: z.number().nullable(),
    max_connection_voltage_kv: z.number().nullable(),
    min_capacity_mw: z.number().nullable(),
    min_connection_voltage_kv: z.number().nullable(),
    plant_type: z.string(),
    run_uid: z.string().nullable(),
    source_clause_uid: z.string().nullable(),
    source_text: z.string().nullable(),
    synchronous_area: z.string().nullable(),
    threshold_uid: z.string()
});

/**
 * Point
 */
export const zPoint = z.object({
    x: z.number(),
    y: z.number()
});

/**
 * ChartLine
 */
export const zChartLine = z.object({
    interpolation: zInterpolation,
    label: z.string(),
    line_key: z.string(),
    points: z.array(zPoint),
    region: zCurveRegion,
    req_uids: z.array(z.string()),
    rule_text: z.string(),
    x_unit: z.string(),
    y_unit: z.string()
});

/**
 * PoolCapacity
 */
export const zPoolCapacity = z.object({
    capacity: z.int().gte(0),
    kinds: z.array(zJobKind),
    leased: z.int().gte(0),
    pool: z.string(),
    queued: z.int().gte(0)
});

/**
 * PreviewFlag
 */
export const zPreviewFlag = z.object({
    count: z.int().gte(0),
    examples: z.array(z.string()).max(3),
    flag: z.string()
});

/**
 * PreviewMetric
 */
export const zPreviewMetric = z.object({
    floor: z.number().nullable(),
    help: z.string(),
    key: z.string(),
    label: z.string(),
    status: z.enum([
        'ok',
        'warn',
        'bad'
    ]),
    target: z.number().nullable(),
    unit: z.enum([
        'count',
        'ratio',
        'pages',
        'score'
    ]),
    value: z.number()
});

/**
 * PreviewNode
 */
export const zPreviewNode = z.object({
    depth: z.int().gte(0),
    excerpt: z.string(),
    kind: z.string(),
    number: z.string(),
    page: z.int(),
    title: z.string().nullable()
});

/**
 * PreviewSection
 */
export const zPreviewSection = z.object({
    chars: z.int().gte(0),
    clauses: z.int().gte(0),
    code: z.string(),
    extract: z.string(),
    page_end: z.int(),
    page_start: z.int(),
    priority: z.string(),
    source: z.enum(['registry', 'profile']),
    title: z.string().nullable()
});

/**
 * ProblemFieldError
 */
export const zProblemFieldError = z.object({
    loc: z.array(z.union([z.string(), z.int()])),
    msg: z.string(),
    type: z.string()
});

/**
 * Problem
 */
export const zProblem = z.object({
    code: z.string(),
    detail: z.string().nullable(),
    errors: z.array(zProblemFieldError).nullable(),
    instance: z.string().nullable(),
    status: z.int().gte(100).lte(599),
    title: z.string(),
    trace_id: z.string().nullable(),
    type: z.string()
});

/**
 * AssistantErrorEvent
 */
export const zAssistantErrorEvent = z.object({
    event: z.literal('error').default('error'),
    problem: zProblem
});

/**
 * CapabilityUploadResult
 */
export const zCapabilityUploadResult = z.object({
    capability_document: zCapabilityDocumentDetail,
    deduplicated: z.boolean(),
    job_id: z.string().nullable(),
    reading_problem: zProblem.nullable()
});

/**
 * ProfileStatus
 */
export const zProfileStatus = z.object({
    adapter_recommended: z.string().nullable(),
    error: z.string().nullable(),
    finished_at: z.iso.datetime({ offset: true }).nullable(),
    job_id: z.string().nullable(),
    profile_sha256: z.string().nullable(),
    profiler_version: z.string().nullable(),
    state: z.enum([
        'none',
        'queued',
        'running',
        'ready',
        'failed'
    ])
});

/**
 * PromoteRequest
 */
export const zPromoteRequest = z.object({
    confirm_removals: z.boolean().optional().default(false),
    valid_from: z.iso.date()
});

/**
 * PromoteResult
 */
export const zPromoteResult = z.object({
    doc_uid: z.string(),
    superseded_doc_uid: z.string().nullable()
});

/**
 * Provenance
 */
export const zProvenance = z.object({
    confidence: z.number(),
    created_by_run: z.string().nullable(),
    created_run_started_at: z.iso.datetime({ offset: true }).nullable(),
    extraction_method: zExtractionMethod,
    extraction_model: z.string(),
    last_run_started_at: z.iso.datetime({ offset: true }).nullable(),
    parameter_mapping_method: zParameterMappingMethod.nullable(),
    prompt_version: z.string(),
    proposed_parameter: z.string().nullable(),
    recheck_reason: z.string().nullable(),
    run_uid: z.string().nullable()
});

/**
 * QualityFlag
 */
export const zQualityFlag = z.enum([
    'low_confidence',
    'citation_not_verbatim',
    'number_not_in_source',
    'base_other',
    'base_missing',
    'reference_point_other',
    'condition_other',
    'applicability_issue',
    'parameter_uncertain',
    'recheck_dropped',
    'model_fallback',
    'curve_table_mismatch',
    'figure_digitized',
    'figure_not_found',
    'figure_not_digitized',
    'translation_unverified',
    'obligation_scan_pivot_only',
    'obligation_scan_unavailable',
    'language_mismatch',
    'synthetic_numbering'
]);

/**
 * QualityStats
 */
export const zQualityStats = z.object({
    clauses_by_flag: z.record(z.string(), z.int()),
    clauses_flagged: z.int(),
    low_confidence: z.int(),
    requirements_by_flag: z.record(z.string(), z.int()),
    requirements_flagged: z.int()
});

/**
 * QuantityKind
 */
export const zQuantityKind = z.enum([
    'voltage',
    'active_power',
    'reactive_power',
    'apparent_power',
    'current',
    'reactive_current',
    'frequency',
    'rate_of_change_of_frequency',
    'time',
    'power_factor',
    'angle',
    'ratio',
    'droop',
    'ramp_rate',
    'energy',
    'harmonics',
    'flicker',
    'temperature',
    'boolean',
    'count',
    'other'
]);

/**
 * Band
 */
export const zBand = z.object({
    band_uid: z.string(),
    base: zBaseQuantity,
    behaviour: z.string().nullable(),
    duration_text: z.string().nullable(),
    lower: z.number().nullable(),
    max_duration_s: z.number().nullable(),
    min_duration_s: z.number().nullable(),
    quantity_kind: zQuantityKind,
    req_uid: z.string(),
    run_uid: z.string().nullable(),
    unit: z.string().nullable(),
    upper: z.number().nullable()
});

/**
 * BandRow
 */
export const zBandRow = z.object({
    band_uid: z.string(),
    base: zBaseQuantity,
    behaviour: z.string().nullable(),
    clause_number: z.string(),
    clause_uid: z.string(),
    duration_text: z.string().nullable(),
    lower: z.number().nullable(),
    max_duration_s: z.number().nullable(),
    min_duration_s: z.number().nullable(),
    quantity_kind: zQuantityKind,
    req_uid: z.string(),
    statement: z.string(),
    unit: z.string().nullable(),
    upper: z.number().nullable()
});

/**
 * ChartAxis
 */
export const zChartAxis = z.object({
    base: zBaseQuantity,
    label: z.string(),
    quantity: zQuantityKind,
    unit: z.string()
});

/**
 * ChartAxes
 */
export const zChartAxes = z.object({
    x: zChartAxis,
    y: zChartAxis
});

/**
 * ChartDetail
 */
export const zChartDetail = z.object({
    axes: zChartAxes,
    chart_key: z.string(),
    cited_by: z.array(zChartCitation),
    cited_by_count: z.int(),
    clause_number: z.string().nullable(),
    clause_uid: z.string().nullable(),
    designation: z.string().nullable(),
    figure_key: z.string().nullable(),
    kind: zChartKind,
    line_count: z.int(),
    lines: z.array(zChartLine),
    page: z.int().nullable(),
    priorities: z.array(z.enum([
        'P1',
        'P2',
        'P3'
    ])),
    title: z.string(),
    topic: zChartTopic
});

/**
 * CurveSummary
 */
export const zCurveSummary = z.object({
    chart_key: z.string().default(''),
    clause_number: z.string().nullable(),
    clause_uid: z.string().nullable(),
    confidence: z.number(),
    curve_type: zCurveType,
    curve_uid: z.string(),
    digitized_by: zDigitizedBy,
    family: zFamilyRef.nullable(),
    figure_key: z.string().nullable(),
    figure_uid: z.string().nullable(),
    interpolation: zInterpolation,
    name: z.string(),
    owner_uid: z.string(),
    point_count: z.int(),
    points: z.array(zPoint),
    region: zCurveRegion,
    req_uid: z.string().nullable(),
    source_kind: zCurveSource,
    source_page: z.int().nullable(),
    statement: z.string().nullable(),
    table_uid: z.string().nullable(),
    verified_by: z.string().nullable(),
    x_base: zBaseQuantity,
    x_quantity: zQuantityKind,
    x_unit: z.string(),
    y_base: zBaseQuantity,
    y_quantity: zQuantityKind,
    y_unit: z.string()
});

/**
 * CurveOut
 */
export const zCurveOut = z.object({
    chart_key: z.string().default(''),
    clause_number: z.string().nullable(),
    clause_uid: z.string().nullable(),
    confidence: z.number(),
    curve_type: zCurveType,
    curve_uid: z.string(),
    digitized_by: zDigitizedBy,
    family: zFamilyRef.nullable(),
    family_members: z.array(zCurveSummary),
    figure_key: z.string().nullable(),
    figure_uid: z.string().nullable(),
    interpolation: zInterpolation,
    name: z.string(),
    owner_uid: z.string(),
    point_count: z.int(),
    points: z.array(zPoint),
    region: zCurveRegion,
    req_uid: z.string().nullable(),
    requirement: zCurveRequirement.nullable(),
    source_kind: zCurveSource,
    source_page: z.int().nullable(),
    statement: z.string().nullable(),
    table_uid: z.string().nullable(),
    verified_at: z.iso.datetime({ offset: true }).nullable(),
    verified_by: z.string().nullable(),
    x_base: zBaseQuantity,
    x_quantity: zQuantityKind,
    x_unit: z.string(),
    y_base: zBaseQuantity,
    y_quantity: zQuantityKind,
    y_unit: z.string()
});

/**
 * MarketBand
 */
export const zMarketBand = z.object({
    band_uid: z.string(),
    base: zBaseQuantity,
    behaviour: z.string().nullable(),
    duration_text: z.string().nullable(),
    lower: z.number().nullable(),
    max_duration_s: z.number().nullable(),
    min_duration_s: z.number().nullable(),
    quantity_kind: zQuantityKind,
    unit: z.string().nullable(),
    upper: z.number().nullable()
});

/**
 * Page[BandRow]
 */
export const zPageBandRow = z.object({
    items: z.array(zBandRow),
    limit: z.int(),
    next_cursor: z.string().nullable(),
    total: z.int()
});

/**
 * Page[CurveSummary]
 */
export const zPageCurveSummary = z.object({
    items: z.array(zCurveSummary),
    limit: z.int(),
    next_cursor: z.string().nullable(),
    total: z.int()
});

/**
 * Parameter
 */
export const zParameter = z.object({
    approved_by: z.string().nullable(),
    default_unit: z.string().nullable(),
    description: z.string(),
    group_id: z.string(),
    ieee2800_reference: z.string().nullable(),
    name: z.string(),
    parameter_id: z.string(),
    proposed_by: z.string().nullable(),
    quantity_kind: zQuantityKind,
    replaced_by: z.string().nullable(),
    rfg_reference: z.string().nullable(),
    status: zParameterStatus.default('approved')
});

/**
 * ParameterRow
 */
export const zParameterRow = z.object({
    approved_by: z.string().nullable(),
    default_unit: z.string().nullable(),
    description: z.string(),
    group_id: z.string(),
    ieee2800_reference: z.string().nullable(),
    is_seed: z.boolean(),
    name: z.string(),
    parameter_id: z.string(),
    proposed_by: z.string().nullable(),
    quantity_kind: zQuantityKind,
    replaced_by: z.string().nullable(),
    requirement_count: z.int(),
    rfg_reference: z.string().nullable(),
    status: zParameterStatus,
    synonym_count: z.int()
});

/**
 * DefinitionDetail
 */
export const zDefinitionDetail = z.object({
    defined_in: zClauseRef.nullable(),
    definition: zDefinition,
    related_parameters: z.array(zParameterRow),
    terms_used: z.array(zDefinitionRef),
    usage_count: z.int(),
    used_by: z.array(zClauseRef)
});

/**
 * Page[ParameterRow]
 */
export const zPageParameterRow = z.object({
    items: z.array(zParameterRow),
    limit: z.int(),
    next_cursor: z.string().nullable(),
    total: z.int()
});

/**
 * ReaderSummary
 */
export const zReaderSummary = z.object({
    boundary: z.string(),
    coherence: z.number().gte(0).lte(1),
    grammar_example: z.string(),
    numbering_family: z.string(),
    sections: z.int().gte(0),
    stamp_template: z.string().nullable(),
    title_placement: z.string()
});

/**
 * Readiness
 */
export const zReadiness = z.object({
    neo4j: z.literal('ok').default('ok'),
    postgres: z.literal('ok').default('ok'),
    schema_version: z.string(),
    status: z.literal('ok').default('ok'),
    store_migration: z.string().nullable()
});

/**
 * Rect
 */
export const zRect = z.object({
    x0: z.number(),
    x1: z.number(),
    y0: z.number(),
    y1: z.number()
});

/**
 * ClauseMark
 */
export const zClauseMark = z.object({
    clause_uid: z.string(),
    number: z.string(),
    rects: z.array(zRect)
});

/**
 * LocateMatch
 */
export const zLocateMatch = z.object({
    found: z.boolean(),
    key: z.string(),
    rects: z.array(zRect)
});

/**
 * LocateResult
 */
export const zLocateResult = z.object({
    matches: z.array(zLocateMatch),
    page_height: z.number(),
    page_width: z.number()
});

/**
 * ReferencePoint
 */
export const zReferencePoint = z.enum([
    'connection_point',
    'interface_point',
    'unit_terminals',
    'unit_transformer_hv',
    'collection_busbar',
    'not_applicable',
    'other'
]);

/**
 * ReferenceRef
 */
export const zReferenceRef = z.object({
    designation: z.string().nullable(),
    label: z.enum(['Clause', 'ExternalStandard']),
    method: z.string().nullable(),
    number: z.string().nullable(),
    raw_text: z.string().nullable(),
    title: z.string().nullable(),
    uid: z.string()
});

/**
 * ReferenceStats
 */
export const zReferenceStats = z.object({
    resolution_rate: z.number().nullable(),
    to_clauses: z.int(),
    to_standards: z.int(),
    unresolved: z.int()
});

/**
 * ReferenceSummary
 */
export const zReferenceSummary = z.object({
    count: z.int().gte(0),
    resolution_rate: z.number().gte(0).lte(1),
    unresolved_sample: z.array(z.string())
});

/**
 * PreviewReport
 */
export const zPreviewReport = z.object({
    adapter: z.string(),
    assist_recommended: z.boolean(),
    band: z.enum([
        'green',
        'amber',
        'red'
    ]),
    created_at: z.iso.datetime({ offset: true }),
    doc_uid: z.string().nullable(),
    estimate_inputs: zEstimateInputs,
    flags: z.array(zPreviewFlag),
    floor_violations: z.array(z.string()),
    furniture: z.array(zFurniturePattern),
    glossary: zGlossarySummary,
    job_id: z.string().nullable(),
    language: zLanguageSummary,
    metrics: z.array(zPreviewMetric),
    profile_sha256: z.string().nullable(),
    profiler_version: z.string(),
    reader: zReaderSummary,
    references: zReferenceSummary,
    sample: z.array(zPreviewNode).max(400),
    score: z.number().gte(0).lte(100),
    sections: z.array(zPreviewSection),
    sha256: z.string()
});

/**
 * RefersTo
 */
export const zRefersTo = z.enum([
    'wtg',
    'ppc',
    'poc',
    'none'
]);

/**
 * LevelChangeIn
 */
export const zLevelChangeIn = z.object({
    headline: z.string().min(1).max(200),
    rationale: z.string().max(1200).optional().default(''),
    refers_to: zRefersTo.optional().default('none'),
    status: zLevelVerdictStatus
});

/**
 * LevelChanges
 */
export const zLevelChanges = z.object({
    poc: zLevelChangeIn.nullish(),
    ppc: zLevelChangeIn.nullish(),
    wtg: zLevelChangeIn.nullish()
});

/**
 * RegistrySummary
 */
export const zRegistrySummary = z.object({
    authorities: z.int().gte(0),
    families: z.int().gte(0),
    head_hash: z.string(),
    jurisdictions: z.int().gte(0),
    revision: z.int().gte(0),
    updated_at: z.iso.datetime({ offset: true }).nullable(),
    versions: z.int().gte(0)
});

/**
 * RenumberedClause
 */
export const zRenumberedClause = z.object({
    new_number: z.string(),
    old_number: z.string()
});

/**
 * RequirementImpactKind
 */
export const zRequirementImpactKind = z.enum(['changed', 'removed']);

/**
 * RequirementImpact
 */
export const zRequirementImpact = z.object({
    change: zRequirementImpactKind,
    changes: z.array(zFieldChange),
    clause_number: z.string().nullable(),
    new_req_uid: z.string().nullable(),
    new_statement: z.string().nullable(),
    req_uid: z.string(),
    statement: z.string()
});

/**
 * RequirementRef
 */
export const zRequirementRef = z.object({
    base: zBaseQuantity,
    modality: zModality,
    parameter_id: z.string().nullable(),
    reference_point: zReferencePoint,
    req_uid: z.string(),
    statement: z.string()
});

/**
 * RequirementSort
 */
export const zRequirementSort = z.enum([
    'document',
    'confidence',
    'parameter',
    'modality',
    'clause',
    'group'
]);

/**
 * ReviewAction
 */
export const zReviewAction = z.enum([
    'approve',
    'change',
    'reset'
]);

/**
 * ReviewCounts
 */
export const zReviewCounts = z.object({
    approved: z.int().gte(0),
    changed: z.int().gte(0),
    pending: z.int().gte(0),
    total: z.int().gte(0)
});

/**
 * ReviewState
 */
export const zReviewState = z.enum([
    'pending',
    'approved',
    'changed'
]);

/**
 * Role
 */
export const zRole = z.enum([
    'reader',
    'licensed',
    'confidential',
    'approver',
    'operator',
    'admin'
]);

/**
 * Invitation
 */
export const zInvitation = z.object({
    accepted_at: z.iso.datetime({ offset: true }).nullable(),
    created_at: z.iso.datetime({ offset: true }),
    email: z.string(),
    expires_at: z.iso.datetime({ offset: true }),
    invitation_id: z.string(),
    invited_by: zActorRef,
    last_sent_at: z.iso.datetime({ offset: true }),
    roles: z.array(zRole),
    sent_count: z.int().gte(1),
    status: zInvitationStatus
});

/**
 * InvitationCreate
 */
export const zInvitationCreate = z.object({
    email: z.email(),
    roles: z.array(zRole).min(1)
});

/**
 * InvitationIssued
 */
export const zInvitationIssued = z.object({
    email_sent: z.boolean(),
    invitation: zInvitation,
    invite_url: z.string()
});

/**
 * InvitationPreview
 */
export const zInvitationPreview = z.object({
    email: z.string(),
    expires_at: z.iso.datetime({ offset: true }),
    invited_by_name: z.string(),
    roles: z.array(zRole)
});

/**
 * Me
 */
export const zMe = z.object({
    dev_mode: z.boolean(),
    email: z.string(),
    initials: z.string(),
    licence_classes: z.array(zLicenceClass),
    mfa_enrolled_at: z.iso.datetime({ offset: true }).nullable(),
    name: z.string(),
    password_changed_at: z.iso.datetime({ offset: true }).nullable(),
    roles: z.array(zRole),
    user_id: z.string()
});

/**
 * Page[Invitation]
 */
export const zPageInvitation = z.object({
    items: z.array(zInvitation),
    limit: z.int(),
    next_cursor: z.string().nullable(),
    total: z.int()
});

/**
 * RollbackImpact
 */
export const zRollbackImpact = z.object({
    assessed_requirements: z.int(),
    later_runs: z.array(z.string())
});

/**
 * RollbackJobRequest
 */
export const zRollbackJobRequest = z.object({
    force: z.boolean().optional().default(false),
    kind: z.literal('rollback'),
    run_uid: z.string()
});

/**
 * RunBrief
 */
export const zRunBrief = z.object({
    cost_usd: z.number().gte(0),
    finished_at: z.iso.datetime({ offset: true }).nullable(),
    run_uid: z.string(),
    started_at: z.iso.datetime({ offset: true }),
    status: z.string()
});

/**
 * RunConfigOut
 */
export const zRunConfigOut = z.object({
    adapter: z.string().nullable(),
    aura_snapshot_id: z.string().nullable(),
    clauses: z.array(z.string()),
    config_sha256: z.string().nullable(),
    flagged_only: z.boolean(),
    mode: z.string().nullable(),
    overrides: z.record(z.string(), z.unknown()),
    pipeline: z.record(z.string(), z.unknown()),
    priorities: z.array(z.string()),
    source_sha256: z.string().nullable()
});

/**
 * RunError
 */
export const zRunError = z.object({
    at: z.iso.datetime({ offset: true }),
    error_type: z.string(),
    message: z.string(),
    step: z.string().nullish()
});

/**
 * RunLock
 */
export const zRunLock = z.object({
    acquired_at: z.iso.datetime({ offset: true }),
    doc_uid: z.string(),
    expires_at: z.iso.datetime({ offset: true }),
    holder: z.string()
});

/**
 * RunStatus
 */
export const zRunStatus = z.enum([
    'running',
    'succeeded',
    'partial',
    'failed',
    'rolled_back'
]);

/**
 * RunTokens
 */
export const zRunTokens = z.object({
    cache_read: z.int(),
    cache_write: z.int(),
    embedding: z.int(),
    input: z.int(),
    output: z.int()
});

/**
 * RunSummary
 */
export const zRunSummary = z.object({
    clause_count: z.int(),
    cost_usd: z.number(),
    current_step: z.string().nullable(),
    doc_uid: z.string(),
    duration_s: z.number().nullable(),
    effective_status: z.enum([
        'running',
        'succeeded',
        'partial',
        'failed',
        'rolled_back',
        'interrupted'
    ]),
    embedding_model: z.string().nullable(),
    error: z.string().nullable(),
    family_id: z.string(),
    finished_at: z.iso.datetime({ offset: true }).nullable(),
    llm_models: z.array(z.string()),
    mode: z.string().nullable(),
    priorities: z.array(z.string()),
    run_uid: z.string(),
    started_at: z.iso.datetime({ offset: true }),
    status: zRunStatus,
    steps_completed: z.int(),
    steps_total: z.int(),
    tokens: zRunTokens,
    version_label: z.string()
});

/**
 * RunUsage
 */
export const zRunUsage = z.object({
    cache_read_tokens: z.int().optional().default(0),
    cache_write_tokens: z.int().optional().default(0),
    code_version: z.string(),
    cost_usd: z.number().optional().default(0),
    embedding_model: z.string().nullish(),
    embedding_tokens: z.int().optional().default(0),
    finished_at: z.iso.datetime({ offset: true }).nullish(),
    input_tokens: z.int().optional().default(0),
    llm_models: z.array(z.string()).optional(),
    llm_provider: z.string(),
    output_tokens: z.int().optional().default(0),
    prompt_versions: z.array(z.string()).optional(),
    run_uid: z.string(),
    started_at: z.iso.datetime({ offset: true }),
    status: z.string(),
    steps_completed: z.array(z.string()).optional()
});

/**
 * AuditReport
 */
export const zAuditReport = z.object({
    acceptance: z.array(zAcceptanceCriterion).optional(),
    acceptance_met: z.boolean(),
    blocking_failures: z.array(z.string()).optional(),
    completeness_gap_count: z.int(),
    completeness_gaps: z.array(z.record(z.string(), z.unknown())).optional(),
    coverage: z.record(z.string(), z.unknown()),
    doc_uid: z.string(),
    failed_clauses: z.int().optional().default(0),
    generated_at: z.iso.datetime({ offset: true }),
    integrity: z.record(z.string(), z.int()),
    passed: z.boolean(),
    priorities: z.array(z.string()),
    recorded_issues: z.record(z.string(), z.int()).optional(),
    run_uid: z.string().nullish(),
    unscanned_clauses: z.int().optional().default(0),
    usage: zRunUsage.nullish(),
    xref_resolution_rate: z.number().nullish(),
    xref_target: z.number()
});

/**
 * SchemaStatusOut
 */
export const zSchemaStatusOut = z.object({
    applied_at: z.string().nullable(),
    applied_by: z.string().nullable(),
    database: z.string(),
    edition: z.string(),
    indexes: z.array(zIndexStatus),
    migrations: z.array(z.string()),
    node_counts: z.record(z.string(), z.int()),
    reasons: z.array(z.string()),
    schema_version: z.string().nullable(),
    state: z.enum([
        'current',
        'out of date',
        'newer than the code',
        'applied by other code'
    ])
});

/**
 * SearchHit
 */
export const zSearchHit = z.object({
    clause_uid: z.string(),
    definitions: z.array(zDefinitionRef),
    document: zDocumentRef,
    fused_score: z.number(),
    kind: zClauseKind,
    language: z.string(),
    number: z.string(),
    page_end: z.int(),
    page_start: z.int(),
    parent: zClauseRef.nullable(),
    pinned: z.boolean().default(false),
    rank: z.int().gte(1),
    ranks: z.record(z.string(), z.int()),
    references: z.array(zReferenceRef),
    requirements: z.array(zRequirementRef),
    rerank_score: z.number().nullable(),
    score: z.number(),
    snippet_en: z.string().nullable(),
    text: z.string(),
    title: z.string().nullable()
});

/**
 * SearchResponse
 */
export const zSearchResponse = z.object({
    hits: z.array(zSearchHit),
    mode: z.enum(['hybrid', 'fulltext']),
    notice: z.string().nullable(),
    query: z.string(),
    reranked: z.boolean()
});

/**
 * SectionChangeCheck
 */
export const zSectionChangeCheck = z.object({
    changed_without_new_stamp: z.array(z.string()),
    new_doc_uid: z.string(),
    new_stamp_without_changes: z.array(z.string()),
    old_doc_uid: z.string(),
    passed: z.boolean(),
    sections_with_changed_clauses: z.array(z.string()),
    sections_with_new_stamp: z.array(z.string()),
    unstamped_sections: z.array(z.string())
});

/**
 * SectionContents
 */
export const zSectionContents = z.object({
    clauses: z.int().gte(0),
    code: z.string(),
    definitions: z.int().gte(0),
    extract: z.string().nullable(),
    extracted: z.int().gte(0),
    failed: z.int().gte(0),
    flagged: z.int().gte(0),
    in_scope: z.int().gte(0),
    modality: z.record(z.string(), z.int()),
    priority: z.string().nullable(),
    requirements: z.int().gte(0),
    title: z.string().nullable()
});

/**
 * SectionDefaults
 */
export const zSectionDefaults = z.object({
    extract: z.enum([
        'requirements',
        'definitions',
        'none'
    ]),
    priority: z.enum([
        'P1',
        'P2',
        'P3'
    ])
});

/**
 * FamilyPatch
 */
export const zFamilyPatch = z.object({
    adapter: z.enum(['generic', 'neso_gb']).nullish(),
    default_scope: zSectionDefaults.nullish(),
    doc_type: zDocType.nullish(),
    implements: z.array(z.string()).nullish(),
    jurisdictions: z.array(z.string().max(32).regex(/^[A-Z][A-Z0-9]*(-[A-Z0-9]+)*$/)).min(1).nullish(),
    landing_page: z.url().min(1).max(2083).nullish(),
    language: z.string().nullish(),
    licence_class: zLicenceClass.nullish(),
    publisher: z.string().max(32).regex(/^[A-Z][A-Z0-9]*(-[A-Z0-9]+)*$/).nullish(),
    publisher_hints: z.string().max(4000).nullish(),
    region_primary: z.boolean().nullish(),
    title: z.string().min(1).max(300).nullish()
});

/**
 * SectionScope
 */
export const zSectionScope = z.object({
    code: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._-]{0,23}$/),
    extract: z.enum([
        'requirements',
        'definitions',
        'none'
    ]),
    load_clauses: z.boolean().default(true),
    normative: z.boolean().default(true),
    priority: z.enum([
        'P1',
        'P2',
        'P3'
    ]),
    title: z.string()
});

/**
 * SectionScopeIn
 */
export const zSectionScopeIn = z.object({
    code: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._-]{0,23}$/),
    extract: z.enum([
        'requirements',
        'definitions',
        'none'
    ]),
    load_clauses: z.boolean().optional().default(true),
    normative: z.boolean().optional().default(true),
    priority: z.enum([
        'P1',
        'P2',
        'P3'
    ]),
    title: z.string().min(1).max(300)
});

/**
 * SectionScopeOut
 */
export const zSectionScopeOut = z.object({
    code: z.string(),
    extract: z.enum([
        'requirements',
        'definitions',
        'none'
    ]),
    load_clauses: z.boolean(),
    normative: z.boolean(),
    priority: z.enum([
        'P1',
        'P2',
        'P3'
    ]),
    title: z.string()
});

/**
 * SectionStats
 */
export const zSectionStats = z.object({
    clauses: z.int(),
    extract: z.enum([
        'requirements',
        'definitions',
        'none'
    ]).nullable(),
    extracted: z.int(),
    failed: z.int(),
    informative: z.int(),
    not_in_scope: z.int(),
    page_end: z.int().nullable(),
    page_start: z.int().nullable(),
    pending: z.int(),
    priority: z.enum([
        'P1',
        'P2',
        'P3'
    ]).nullable(),
    requirements: z.int(),
    reused: z.int(),
    section_code: z.string(),
    title: z.string()
});

/**
 * SectionsIn
 */
export const zSectionsIn = z.object({
    sections: z.array(zSectionScopeIn).max(500)
});

/**
 * SettingChoice
 */
export const zSettingChoice = z.object({
    label: z.string(),
    value: z.string()
});

/**
 * SettingModelOption
 */
export const zSettingModelOption = z.object({
    label: z.string(),
    levels: z.array(zSettingChoice),
    offered: z.boolean(),
    value: z.string()
});

/**
 * SettingMoney
 */
export const zSettingMoney = z.object({
    amount: z.number(),
    currency: z.enum(['EUR', 'USD'])
});

/**
 * SettingTaskModel
 */
export const zSettingTaskModel = z.object({
    effort: z.enum([
        'low',
        'medium',
        'high',
        'xhigh',
        'max'
    ]).nullable(),
    model: z.string()
});

/**
 * Setting
 */
export const zSetting = z.object({
    choices: z.array(zSettingChoice).nullable(),
    confirm: z.string().nullable(),
    confirm_choices: z.array(z.string()).nullable(),
    default: z.union([
        z.boolean(),
        z.int(),
        z.number(),
        z.array(z.string()),
        zSettingMoney,
        zSettingTaskModel
    ]).nullable(),
    description: z.string(),
    exclusive_minimum: z.number().nullable(),
    key: z.string(),
    kind: z.enum([
        'boolean',
        'integer',
        'number',
        'duration',
        'bytes',
        'money',
        'choices',
        'model'
    ]),
    label: z.string(),
    locked: z.boolean(),
    maximum: z.number().nullable(),
    minimum: z.number().nullable(),
    models: z.array(zSettingModelOption).nullable(),
    null_label: z.string().nullable(),
    nullable: z.boolean(),
    overridden: z.boolean(),
    recommended: zSettingTaskModel.nullable(),
    section: z.string().nullable(),
    stale: z.boolean(),
    stale_message: z.string().nullable(),
    unit: z.string().nullable(),
    value: z.union([
        z.boolean(),
        z.int(),
        z.number(),
        z.array(z.string()),
        zSettingMoney,
        zSettingTaskModel
    ]).nullable()
});

/**
 * SettingChange
 */
export const zSettingChange = z.object({
    from: z.union([
        z.boolean(),
        z.int(),
        z.number(),
        z.array(z.string()),
        zSettingMoney,
        zSettingTaskModel
    ]).nullable(),
    key: z.string(),
    label: z.string(),
    to: z.union([
        z.boolean(),
        z.int(),
        z.number(),
        z.array(z.string()),
        zSettingMoney,
        zSettingTaskModel
    ]).nullable()
});

/**
 * SettingsGroup
 */
export const zSettingsGroup = z.object({
    description: z.string(),
    key: z.string(),
    settings: z.array(zSetting),
    title: z.string()
});

/**
 * SettingsHistoryEntry
 */
export const zSettingsHistoryEntry = z.object({
    actor_name: z.string(),
    at: z.iso.datetime({ offset: true }),
    changes: z.array(zSettingChange),
    event_id: z.string()
});

/**
 * SettingsPage
 */
export const zSettingsPage = z.object({
    etag: z.string(),
    groups: z.array(zSettingsGroup),
    revision: z.int().gte(0),
    updated_at: z.iso.datetime({ offset: true }).nullable(),
    updated_by_name: z.string().nullable()
});

/**
 * SettingsUpdate
 */
export const zSettingsUpdate = z.object({
    changes: z.record(z.string(), zJsonValue)
});

/**
 * SiblingRequirement
 */
export const zSiblingRequirement = z.object({
    item_ref: z.string().nullable(),
    local_index: z.int(),
    req_uid: z.string(),
    statement: z.string()
});

/**
 * SignInNext
 */
export const zSignInNext = z.enum(['code', 'setup']);

/**
 * SignInRequest
 */
export const zSignInRequest = z.object({
    email: z.email(),
    password: z.string().min(1).max(1024)
});

/**
 * SignInStep
 */
export const zSignInStep = z.object({
    expires_at: z.iso.datetime({ offset: true }),
    next: zSignInNext
});

/**
 * SortDirection
 */
export const zSortDirection = z.enum(['asc', 'desc']);

/**
 * SourceChange
 */
export const zSourceChange = z.object({
    added: z.array(zPdfLink).optional(),
    detected_at: z.iso.datetime({ offset: true }),
    family_id: z.string(),
    landing_page: z.string(),
    record: z.string().nullish(),
    registered_versions: z.array(z.string()).optional(),
    removed: z.array(zPdfLink).optional(),
    retitled: z.array(zPdfLink).optional()
});

/**
 * SourceFamilyCore
 */
export const zSourceFamilyCore = z.object({
    adapter: z.string(),
    default_scope: zSectionDefaults,
    doc_type: zDocType,
    family_id: z.string(),
    implements: z.array(z.string()),
    jurisdictions: z.array(z.string()),
    landing_page: z.string().nullable(),
    language: z.string(),
    licence_class: zLicenceClass,
    publisher: z.string(),
    publisher_hints: z.string().nullable(),
    region_primary: z.boolean(),
    sections: z.array(zSectionScope),
    title: z.string()
});

/**
 * SourceFigure
 */
export const zSourceFigure = z.object({
    caption: z.string().nullable(),
    chart_key: z.string().nullable(),
    curves: z.array(z.string()),
    doc_uid: z.string(),
    figure_key: z.string(),
    figure_uid: z.string(),
    page: z.int()
});

/**
 * AssistantSource
 */
export const zAssistantSource = z.object({
    clause_uid: z.string(),
    definition_uid: z.string().nullable(),
    document: zDocumentRef,
    figures: z.array(zSourceFigure),
    index: z.int(),
    kind: z.enum(['clause', 'definition']),
    number: z.string(),
    page_end: z.int(),
    page_start: z.int(),
    requirement_uids: z.array(z.string()),
    rerank_score: z.number().nullable(),
    score: z.number().nullable(),
    snippet: z.string().max(400),
    title: z.string().nullable()
});

/**
 * AssistantMessage
 */
export const zAssistantMessage = z.object({
    blocks: z.array(zAssistantBlock),
    content: z.string(),
    created_at: z.iso.datetime({ offset: true }),
    error: zProblem.nullable(),
    message_id: z.string(),
    model: z.string().nullable(),
    notices: z.array(zAssistantNotice),
    outcome: zAssistantOutcome.nullable(),
    reply_to: z.string().nullable(),
    role: z.enum(['user', 'assistant']),
    seq: z.int(),
    sources: z.array(zAssistantSource),
    status: z.enum([
        'complete',
        'failed',
        'cancelled',
        'no_llm'
    ]),
    thread_id: z.string(),
    usage: zAssistantUsage.nullable()
});

/**
 * AssistantSourcesEvent
 */
export const zAssistantSourcesEvent = z.object({
    event: z.literal('sources').default('sources'),
    sources: z.array(zAssistantSource)
});

/**
 * AssistantEvent
 */
export const zAssistantEvent = z.discriminatedUnion('event', [
    zAssistantMetaEvent,
    zAssistantSourcesEvent,
    zAssistantStatusEvent,
    zAssistantDeltaEvent,
    zAssistantCitationEvent,
    zAssistantNoticeEvent,
    zAssistantUsageEvent,
    zAssistantDoneEvent,
    zAssistantErrorEvent
]);

/**
 * AssistantThreadDetail
 */
export const zAssistantThreadDetail = z.object({
    created_at: z.iso.datetime({ offset: true }),
    message_count: z.int(),
    messages: z.array(zAssistantMessage),
    scope: zAnswerScope,
    thread_id: z.string(),
    title: z.string(),
    updated_at: z.iso.datetime({ offset: true })
});

/**
 * SourceFileOut
 */
export const zSourceFileOut = z.object({
    aliases: z.array(z.string()),
    encrypted: z.boolean().nullable(),
    file_name: z.string(),
    languages: z.array(z.string()),
    page_count: z.int().nullable(),
    pdf_title: z.string().nullable(),
    profile: zProfileStatus,
    registered_as: z.array(z.string()),
    sha256: z.string(),
    size_bytes: z.int().gte(0),
    text_layer_ratio: z.number().gte(0).lte(1).nullable(),
    uploaded_at: z.iso.datetime({ offset: true }),
    uploaded_by: zActor
});

/**
 * DocumentContents
 */
export const zDocumentContents = z.object({
    build_projection_sha256: z.string().nullable(),
    build_state: zBuildState,
    content_revision: z.int().gte(0),
    counts: z.record(z.string(), z.int()),
    doc_uid: z.string(),
    file: zSourceFileOut.nullable(),
    flags: z.record(z.string(), z.int()),
    hash_version: z.int(),
    language: z.string(),
    languages: z.array(z.string()),
    lifecycle_status: zDocumentStatus,
    quality_score: z.number().gte(0).lte(100).nullable(),
    ready_at: z.iso.datetime({ offset: true }).nullable(),
    registry_revision: z.int().nullable(),
    runs: z.array(zRunBrief),
    schema_version: z.string(),
    sections: z.array(zSectionContents),
    stale: z.boolean(),
    stale_fields: z.array(z.string()),
    translation: zCoverage.nullable(),
    updating_run: z.string().nullable(),
    vectors: z.record(z.string(), zCoverage)
});

/**
 * Page[SourceFileOut]
 */
export const zPageSourceFileOut = z.object({
    items: z.array(zSourceFileOut),
    limit: z.int(),
    next_cursor: z.string().nullable(),
    total: z.int()
});

/**
 * SourceProfileJobRequest
 */
export const zSourceProfileJobRequest = z.object({
    adapter: z.enum(['generic', 'neso_gb']).nullish(),
    kind: z.literal('source_profile'),
    language: z.string().nullish(),
    sha256: z.string().regex(/^[0-9a-f]{64}$/)
});

/**
 * SourceProfileOut
 */
export const zSourceProfileOut = z.object({
    adapter: z.string(),
    adapter_reason: z.string(),
    adapter_recommended: z.enum(['generic', 'neso_gb']),
    cover: zCoverHints,
    created_at: z.iso.datetime({ offset: true }),
    language: zLanguageDetection,
    profile_sha256: z.string(),
    profiler_version: z.string(),
    proposed_sections: z.array(zEffectiveSection),
    report: zPreviewReport,
    sha256: z.string()
});

/**
 * SourceSnapshot
 */
export const zSourceSnapshot = z.object({
    family_id: z.string(),
    fetched_at: z.iso.datetime({ offset: true }),
    final_url: z.string(),
    landing_page: z.string(),
    links: z.array(zPdfLink).optional()
});

/**
 * SourceVersion
 */
export const zSourceVersion = z.object({
    file: z.string(),
    layout_profile: zLayoutProfileRef.nullable(),
    published_on: z.iso.date().nullable(),
    retrieved_on: z.iso.date().nullable(),
    sha256: z.string().regex(/^[0-9a-f]{64}$/).nullable(),
    source_url: z.string().nullable(),
    status: zDocumentStatus,
    valid_from: z.iso.date().nullable(),
    valid_to: z.iso.date().nullable(),
    version_label: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._-]{0,31}$/)
});

/**
 * SourceVersionOut
 */
export const zSourceVersionOut = z.object({
    build_state: zBuildState.nullable(),
    file_name: z.string(),
    graph_status: zDocumentStatus.nullable(),
    published_on: z.iso.date().nullable(),
    registered_doc_uid: z.string().nullable(),
    sha256: z.string().nullable(),
    status: zDocumentStatus,
    valid_from: z.iso.date().nullable(),
    valid_to: z.iso.date().nullable(),
    version_label: z.string()
});

/**
 * SourceFamilyOut
 */
export const zSourceFamilyOut = z.object({
    adapter: z.string(),
    family_id: z.string(),
    landing_page: z.string().nullable(),
    licence_class: zLicenceClass,
    publisher: z.string(),
    sections: z.array(zSectionScopeOut),
    title: z.string(),
    versions: z.array(zSourceVersionOut)
});

/**
 * SourceWatchJobRequest
 */
export const zSourceWatchJobRequest = z.object({
    kind: z.literal('source_watch')
});

/**
 * JobRequest
 */
export const zJobRequest = z.discriminatedUnion('kind', [
    zIngestJobRequest,
    zIngestResumeJobRequest,
    zIngestDryRunJobRequest,
    zRollbackJobRequest,
    zDeleteVersionJobRequest,
    zConsolidateParametersJobRequest,
    zSourceWatchJobRequest,
    zEvaluationJobRequest,
    zSourceProfileJobRequest,
    zCapabilityExtractRequest,
    zAssessmentRunRequest
]);

/**
 * SourceWatchOut
 */
export const zSourceWatchOut = z.object({
    changes: z.array(zSourceChange),
    last_snapshot: zSourceSnapshot.nullable()
});

/**
 * StandardOut
 */
export const zStandardOut = z.object({
    citation_count: z.int(),
    cited_by: z.array(zClauseRef),
    designation: z.string(),
    publisher: z.string().nullable(),
    standard_id: z.string(),
    title: z.string().nullable()
});

/**
 * StatsCounts
 */
export const zStatsCounts = z.object({
    bands: z.int(),
    clauses: z.int(),
    curve_families: z.int(),
    curves: z.int(),
    definitions: z.int(),
    figures: z.int(),
    open_parameters: z.int(),
    parameters_used: z.int(),
    requirements: z.int(),
    standards: z.int(),
    tables: z.int(),
    thresholds: z.int()
});

/**
 * DocumentStats
 */
export const zDocumentStats = z.object({
    clauses_by_extraction_status: z.record(z.string(), z.int()),
    clauses_by_priority: z.record(z.string(), z.int()),
    counts: zStatsCounts,
    definitions_used: z.int(),
    doc_uid: z.string(),
    quality: zQualityStats,
    references: zReferenceStats,
    requirements_by_group: z.array(zGroupCount),
    requirements_by_modality: z.record(z.string(), z.int()),
    requirements_by_value_type: z.record(z.string(), z.int()),
    sections: z.array(zSectionStats)
});

/**
 * StatusRequest
 */
export const zStatusRequest = z.object({
    status: zDocumentStatus,
    valid_to: z.iso.date().nullish()
});

/**
 * StepOut
 */
export const zStepOut = z.object({
    counters: z.record(z.string(), z.unknown()),
    status: z.enum([
        'done',
        'current',
        'pending',
        'not_planned'
    ]),
    step: z.string()
});

/**
 * StorageOut
 */
export const zStorageOut = z.object({
    backend: z.enum(['local', 'azure_blob']),
    location: z.string().nullable()
});

/**
 * Suggestion
 */
export const zSuggestion = z.object({
    question: z.string()
});

/**
 * Synonym
 */
export const zSynonym = z.object({
    lang: z.string(),
    text: z.string()
});

/**
 * SystemStatus
 */
export const zSystemStatus = z.object({
    api: zApiMeta,
    configuration: zConfigurationReport,
    embeddings: zEmbeddingsOut,
    llm_tasks: z.array(zLlmTaskOut),
    schema: zSchemaStatusOut,
    storage: zStorageOut
});

/**
 * TableOut
 */
export const zTableOut = z.object({
    bbox: z.array(z.number()),
    caption: z.string().nullable(),
    clause_number: z.string().nullable(),
    clause_uid: z.string().nullable(),
    curve_count: z.int(),
    key: z.string(),
    n_cols: z.int(),
    n_rows: z.int(),
    page: z.int(),
    rows: z.array(z.array(z.string())),
    table_uid: z.string()
});

/**
 * TableRow
 */
export const zTableRow = z.object({
    bbox: z.array(z.number()),
    caption: z.string().nullable(),
    clause_number: z.string().nullable(),
    clause_uid: z.string().nullable(),
    curve_count: z.int(),
    key: z.string(),
    n_cols: z.int(),
    n_rows: z.int(),
    page: z.int(),
    table_uid: z.string()
});

/**
 * Page[TableRow]
 */
export const zPageTableRow = z.object({
    items: z.array(zTableRow),
    limit: z.int(),
    next_cursor: z.string().nullable(),
    total: z.int()
});

/**
 * TextMark
 */
export const zTextMark = z.object({
    found: z.boolean(),
    rects: z.array(zRect),
    req_uid: z.string()
});

/**
 * ThreadRename
 */
export const zThreadRename = z.object({
    title: z.string().min(1).max(120)
});

/**
 * TocNode
 */
export const zTocNode = z.object({
    child_count: z.int(),
    clause_uid: z.string(),
    display_title: z.string(),
    extraction_status: zExtractionStatus,
    kind: zClauseKind,
    level: z.int(),
    number: z.string(),
    page_end: z.int(),
    page_start: z.int(),
    priority: z.string().nullable(),
    quality_flags: z.array(z.string()),
    requirement_count: z.int(),
    section_code: z.string(),
    subtree_requirement_count: z.int(),
    title: z.string().nullable()
});

/**
 * TokenUsage
 */
export const zTokenUsage = z.object({
    cache_read: z.int().gte(0).optional().default(0),
    cache_write_1h: z.int().gte(0).optional().default(0),
    cache_write_5m: z.int().gte(0).optional().default(0),
    input: z.int().gte(0).optional().default(0),
    output: z.int().gte(0).optional().default(0)
});

/**
 * LedgerEntry
 */
export const zLedgerEntry = z.object({
    calls: z.int().gte(0).optional().default(0),
    cost_usd: z.number().gte(0).optional().default(0),
    model: z.string(),
    task: z.string(),
    usage: zTokenUsage.optional()
});

/**
 * RunDetail
 */
export const zRunDetail = z.object({
    artifacts: z.array(z.string()),
    code_version: z.string(),
    config: zRunConfigOut,
    errors: z.array(zRunError),
    llm_provider: z.string(),
    lock: zRunLock.nullable(),
    prompt_versions: z.array(z.string()),
    schema_version: z.string(),
    state_available: z.boolean(),
    steps: z.array(zStepOut),
    summary: zRunSummary,
    usage_by_task: z.array(zLedgerEntry)
});

/**
 * ToneCounts
 */
export const zToneCounts = z.object({
    caution: z.int().gte(0).default(0),
    info: z.int().gte(0).default(0),
    muted: z.int().gte(0).default(0),
    negative: z.int().gte(0).default(0),
    positive: z.int().gte(0).default(0),
    positive_soft: z.int().gte(0).default(0)
});

/**
 * LevelVerdictCounts
 */
export const zLevelVerdictCounts = z.object({
    poc: zToneCounts,
    ppc: zToneCounts,
    wtg: zToneCounts
});

/**
 * AssessmentDetail
 */
export const zAssessmentDetail = z.object({
    approval_comment: z.string().nullable(),
    approved_at: z.iso.datetime({ offset: true }).nullable(),
    approved_by_name: z.string().nullable(),
    assessment_uid: z.string(),
    can_delete: z.boolean(),
    capability_document: zAssessmentProduct,
    cost_usd: z.number().nullable(),
    created_at: z.iso.datetime({ offset: true }),
    created_by_name: z.string(),
    error: z.string().nullable(),
    grid_code: zAssessmentGridCode,
    grid_connection: zGridConnection.nullable(),
    in_review: z.boolean(),
    job_id: z.string().nullable(),
    name: z.string(),
    newer_version_available: z.boolean(),
    plant_type: z.enum([
        'A',
        'B',
        'C',
        'D'
    ]).nullable(),
    product_types: z.array(z.string()),
    remaining_topic_keys: z.array(z.string()),
    review: zReviewCounts,
    status: zAssessmentStatus,
    topic_catalogue_version: z.int(),
    topics_total: z.int().gte(0),
    updated_at: z.iso.datetime({ offset: true }),
    verdicts: zLevelVerdictCounts
});

/**
 * AssessmentSummary
 */
export const zAssessmentSummary = z.object({
    approved_at: z.iso.datetime({ offset: true }).nullable(),
    approved_by_name: z.string().nullable(),
    assessment_uid: z.string(),
    capability_document: zAssessmentProduct,
    created_at: z.iso.datetime({ offset: true }),
    created_by_name: z.string(),
    error: z.string().nullable(),
    grid_code: zAssessmentGridCode,
    grid_connection: zGridConnection.nullable(),
    in_review: z.boolean(),
    job_id: z.string().nullable(),
    name: z.string(),
    newer_version_available: z.boolean(),
    plant_type: z.enum([
        'A',
        'B',
        'C',
        'D'
    ]).nullable(),
    review: zReviewCounts,
    status: zAssessmentStatus,
    topics_total: z.int().gte(0),
    updated_at: z.iso.datetime({ offset: true }),
    verdicts: zLevelVerdictCounts
});

/**
 * TopicCheck
 */
export const zTopicCheck = z.enum([
    'no_clause_cited',
    'claim_without_capability',
    'capability_not_applicable'
]);

/**
 * TopicCheckOut
 */
export const zTopicCheckOut = z.object({
    code: zTopicCheck,
    label: z.string()
});

/**
 * TopicHistoryAction
 */
export const zTopicHistoryAction = z.enum([
    'approved',
    'changed',
    'reset'
]);

/**
 * TopicHistoryEntry
 */
export const zTopicHistoryEntry = z.object({
    action: zTopicHistoryAction,
    at: z.iso.datetime({ offset: true }),
    by_name: z.string(),
    comment: z.string().nullable(),
    label: z.string()
});

/**
 * TopicImpact
 */
export const zTopicImpact = z.object({
    clauses: z.array(zClauseImpact),
    display_id: z.string(),
    requirements: z.array(zRequirementImpact),
    title: z.string(),
    topic_key: z.string()
});

/**
 * TopicLabel
 */
export const zTopicLabel = z.object({
    chapter: z.string().nullable(),
    title: z.string()
});

/**
 * FacetLabels
 */
export const zFacetLabels = z.object({
    control_function: z.record(z.string(), zControlFunctionLabel),
    group: z.record(z.string(), z.string()),
    section: z.record(z.string(), z.string()),
    section_priority: z.record(z.string(), z.enum([
        'P1',
        'P2',
        'P3'
    ])),
    topic: z.record(z.string(), zTopicLabel)
});

/**
 * RequirementFacets
 */
export const zRequirementFacets = z.object({
    control_function: z.record(z.string(), z.int()),
    extraction_method: z.record(z.string(), z.int()),
    flagged: zBooleanFacet,
    group: z.record(z.string(), z.int()),
    has_curve: zBooleanFacet,
    has_open_parameters: zBooleanFacet,
    labels: zFacetLabels,
    modality: z.record(z.string(), z.int()),
    plant_type: z.record(z.string(), z.int()),
    priority: z.record(z.string(), z.int()),
    quality_flag: z.record(z.string(), z.int()),
    quantitative: zBooleanFacet,
    reference_point: z.record(z.string(), z.int()),
    section: z.record(z.string(), z.int()),
    technology: z.record(z.string(), z.int()),
    topic: z.record(z.string(), z.int()),
    total: z.int(),
    value_type: z.record(z.string(), z.int())
});

/**
 * TopicReview
 */
export const zTopicReview = z.object({
    action: zReviewAction,
    comment: z.string().max(2000).nullish(),
    levels: zLevelChanges.nullish()
});

/**
 * TotpSetup
 */
export const zTotpSetup = z.object({
    account: z.string(),
    issuer: z.string(),
    otpauth_uri: z.string(),
    qr_svg: z.string(),
    secret: z.string()
});

/**
 * UploadComplete
 */
export const zUploadComplete = z.object({
    profile: z.boolean().optional().default(true)
});

/**
 * UploadCreate
 */
export const zUploadCreate = z.object({
    content_type: z.string(),
    file_name: z.string().min(1).max(255),
    sha256: z.string().regex(/^[0-9a-f]{64}$/).nullish(),
    size_bytes: z.int().gt(0)
});

/**
 * UploadPart
 */
export const zUploadPart = z.object({
    part_number: z.int().gte(1),
    sha256: z.string(),
    size_bytes: z.int().gte(0)
});

/**
 * UploadResult
 */
export const zUploadResult = z.object({
    deduplicated: z.boolean(),
    profile_job_id: z.string().nullable(),
    source_file: zSourceFileOut
});

/**
 * UploadSession
 */
export const zUploadSession = z.object({
    deduplicated_source: zSourceFileOut.nullable(),
    expires_at: z.iso.datetime({ offset: true }),
    file_name: z.string(),
    part_count: z.int(),
    part_size: z.int(),
    received: z.array(z.int()),
    size_bytes: z.int(),
    state: z.enum([
        'receiving',
        'completed',
        'aborted'
    ]),
    upload_id: z.string()
});

/**
 * UserStatus
 */
export const zUserStatus = z.enum(['active', 'disabled']);

/**
 * UserAccount
 */
export const zUserAccount = z.object({
    assistant_budget: zAssistantBudgetOut,
    created_at: z.iso.datetime({ offset: true }),
    email: z.string(),
    last_sign_in_at: z.iso.datetime({ offset: true }).nullable(),
    locked_until: z.iso.datetime({ offset: true }).nullable(),
    mfa_enrolled_at: z.iso.datetime({ offset: true }).nullable(),
    name: z.string(),
    password_changed_at: z.iso.datetime({ offset: true }).nullable(),
    roles: z.array(zRole),
    status: zUserStatus,
    user_id: z.string()
});

/**
 * Page[UserAccount]
 */
export const zPageUserAccount = z.object({
    items: z.array(zUserAccount),
    limit: z.int(),
    next_cursor: z.string().nullable(),
    total: z.int()
});

/**
 * UserUpdate
 */
export const zUserUpdate = z.object({
    name: z.string().min(1).max(120).nullish(),
    roles: z.array(zRole).min(1).nullish(),
    status: zUserStatus.nullish()
});

/**
 * ValueMark
 */
export const zValueMark = z.object({
    found: z.boolean(),
    key: z.string(),
    label: z.string(),
    rects: z.array(zRect)
});

/**
 * PageHighlights
 */
export const zPageHighlights = z.object({
    clause: zClauseMark.nullable(),
    doc_uid: z.string(),
    figures: z.array(zFigureMark),
    page: z.int(),
    page_height: z.number(),
    page_width: z.number(),
    requirement: zTextMark.nullable(),
    rotation: z.int(),
    values: z.array(zValueMark)
});

/**
 * ValueType
 */
export const zValueType = z.enum([
    'scalar',
    'range',
    'curve',
    'curve_family',
    'bands',
    'boolean',
    'text'
]);

/**
 * MarketRequirement
 */
export const zMarketRequirement = z.object({
    access_level: zAccessLevel,
    bands: z.array(zMarketBand),
    base: zBaseQuantity,
    clause_number: z.string(),
    comparator: zComparator,
    curves: z.array(zMarketCurve),
    doc_uid: z.string(),
    family_id: z.string(),
    jurisdiction_id: z.string(),
    modality: zModality,
    open_parameters: z.array(zMarketOpenParameter),
    reference_point: zReferencePoint,
    req_uid: z.string(),
    statement: z.string(),
    unit: z.string().nullable(),
    value: z.number().nullable(),
    value_max: z.number().nullable(),
    value_min: z.number().nullable(),
    value_type: zValueType,
    version_label: z.string()
});

/**
 * OmniboxRequirement
 */
export const zOmniboxRequirement = z.object({
    clause_number: z.string(),
    modality: zModality,
    req_uid: z.string(),
    statement: z.string(),
    value_type: zValueType
});

/**
 * Omnibox
 */
export const zOmnibox = z.object({
    clauses: z.array(zOmniboxClause),
    definitions: z.array(zOmniboxDefinition),
    parameters: z.array(zOmniboxParameter),
    requirements: z.array(zOmniboxRequirement)
});

/**
 * RequirementFilters
 */
export const zRequirementFilters = z.object({
    applies_to_all: z.boolean().nullish(),
    clause_uid: z.string().nullish(),
    control_function: z.array(z.string()).optional(),
    doc_uid: z.string(),
    extraction_method: z.array(zExtractionMethod).optional(),
    flag: z.array(zQualityFlag).optional(),
    flagged: z.boolean().nullish(),
    group: z.string().nullish(),
    has_curve: z.boolean().nullish(),
    has_open_parameters: z.boolean().nullish(),
    max_confidence: z.number().gte(0).lte(1).nullish(),
    min_confidence: z.number().gte(0).lte(1).nullish(),
    modality: z.array(zModality).optional(),
    parameter_id: z.string().nullish(),
    plant_type: z.array(z.string()).optional(),
    priority: z.array(z.enum([
        'P1',
        'P2',
        'P3'
    ])).optional(),
    q: z.string().min(1).max(500).nullish(),
    quantitative: z.boolean().nullish(),
    reference_point: z.array(zReferencePoint).optional(),
    section: z.string().nullish(),
    technology: z.array(z.string()).optional(),
    topic: z.array(z.string()).optional(),
    user_category: z.array(z.string()).optional(),
    value_type: z.array(zValueType).optional()
});

/**
 * VerdictConfidence
 */
export const zVerdictConfidence = z.enum([
    'high',
    'medium',
    'low'
]);

/**
 * VerdictTone
 */
export const zVerdictTone = z.enum([
    'positive',
    'positive_soft',
    'info',
    'caution',
    'negative',
    'muted'
]);

/**
 * EngineLevel
 */
export const zEngineLevel = z.object({
    confidence: zVerdictConfidence.nullable(),
    confidence_reason: z.string(),
    headline: z.string(),
    label: z.string(),
    rationale: z.string(),
    refers_to: zRefersTo,
    status: zLevelVerdictStatus,
    tone: zVerdictTone
});

/**
 * EngineLevels
 */
export const zEngineLevels = z.object({
    poc: zEngineLevel,
    ppc: zEngineLevel,
    wtg: zEngineLevel
});

/**
 * LevelCell
 */
export const zLevelCell = z.object({
    confidence: zVerdictConfidence.nullable(),
    headline: z.string(),
    label: z.string(),
    refers_to: zRefersTo,
    status: zLevelVerdictStatus,
    tone: zVerdictTone
});

/**
 * LevelDetail
 */
export const zLevelDetail = z.object({
    capabilities: z.array(zCitedCapability),
    confidence: zVerdictConfidence.nullable(),
    confidence_reason: z.string(),
    headline: z.string(),
    label: z.string(),
    rationale: z.string(),
    refers_to: zRefersTo,
    status: zLevelVerdictStatus,
    tone: zVerdictTone
});

/**
 * TopicResultDetail
 */
export const zTopicResultDetail = z.object({
    checks: z.array(zTopicCheckOut),
    checks_count: z.int().gte(0),
    clause_numbers: z.array(z.string()),
    clauses: z.array(zClauseRef),
    display_id: z.string(),
    engine: zEngineLevels.nullable(),
    etag: z.string(),
    group: z.string(),
    history: z.array(zTopicHistoryEntry),
    key: z.string(),
    ordinal: z.int().gte(1),
    poc: zLevelDetail,
    ppc: zLevelDetail,
    requirement_found: z.boolean(),
    requirement_summary: z.string(),
    requirements: z.array(zCitedRequirement),
    review_comment: z.string().nullable(),
    review_state: zReviewState,
    reviewed_at: z.iso.datetime({ offset: true }).nullable(),
    reviewed_by_name: z.string().nullable(),
    revision: z.int().gte(1),
    title: z.string(),
    wtg: zLevelDetail
});

/**
 * TopicResultRow
 */
export const zTopicResultRow = z.object({
    checks_count: z.int().gte(0),
    clause_numbers: z.array(z.string()),
    display_id: z.string(),
    group: z.string(),
    key: z.string(),
    ordinal: z.int().gte(1),
    poc: zLevelCell,
    ppc: zLevelCell,
    review_state: zReviewState,
    title: z.string(),
    wtg: zLevelCell
});

/**
 * VerificationMethod
 */
export const zVerificationMethod = z.enum([
    'certificate',
    'type_test',
    'site_test',
    'compliance_test',
    'simulation_rms',
    'simulation_emt',
    'calculation',
    'measurement',
    'document_review',
    'declaration',
    'not_specified'
]);

/**
 * Requirement
 */
export const zRequirement = z.object({
    acceptance: z.string().nullable(),
    access_level: zAccessLevel.default('single'),
    base: zBaseQuantity,
    base_text: z.string().nullable(),
    clause_uid: z.string(),
    comparator: zComparator.default('none'),
    confidence: z.number(),
    depends_on: z.array(z.string()),
    doc_uid: z.string(),
    extraction_method: zExtractionMethod,
    extraction_model: z.string(),
    family_id: z.string(),
    item_ref: z.string().nullable(),
    local_index: z.int(),
    modality: zModality,
    notes: z.string().nullable(),
    obligated_party: z.string().nullable(),
    parameter_id: z.string().nullable(),
    parameter_mapping_method: zParameterMappingMethod.nullable(),
    prompt_version: z.string(),
    proposed_parameter: z.string().nullable(),
    quality_flags: z.array(z.string()),
    quantity_kind: zQuantityKind,
    recheck_reason: z.string().nullable(),
    reference_point: zReferencePoint,
    reference_point_text: z.string().nullable(),
    req_uid: z.string(),
    run_uid: z.string().nullable(),
    source_page: z.int(),
    source_text: z.string(),
    statement: z.string(),
    time_limit_s: z.number().nullable(),
    unit: z.string().nullable(),
    value: z.number().nullable(),
    value_max: z.number().nullable(),
    value_min: z.number().nullable(),
    value_type: zValueType,
    verification_methods: z.array(zVerificationMethod)
});

/**
 * ClauseDetailOut
 */
export const zClauseDetailOut = z.object({
    children: z.array(zTocNode),
    clause: zClause,
    context_loaded: z.boolean(),
    definitions: z.array(zDefinition),
    document: zDocumentRef,
    figures: z.array(zFigureRow),
    history: zClauseHistory,
    path: z.array(zClauseRef),
    referenced_by: z.array(zReferenceRef),
    references: z.array(zReferenceRef),
    requirements: z.array(zRequirement),
    section_title: z.string().nullable(),
    tables: z.array(zTableRow)
});

/**
 * RequirementDetail
 */
export const zRequirementDetail = z.object({
    applicability: zApplicabilityOut,
    bands: z.array(zBand),
    clause: zClauseContext,
    curve_family: zCurveFamily.nullable(),
    curves: z.array(zCurveOut),
    definitions: z.array(zDefinitionRef),
    depends_on: z.array(zDependsOn),
    document: zDocumentRef,
    extracted_values: z.array(zExtractedValue),
    group: zParameterGroup.nullable(),
    open_parameters: z.array(zOpenParameter),
    parameter: zParameter.nullable(),
    provenance: zProvenance,
    requirement: zRequirement,
    siblings: z.array(zSiblingRequirement),
    value_text: z.string()
});

/**
 * RequirementRow
 */
export const zRequirementRow = z.object({
    applicability_summary: z.string().nullable(),
    applies_to_all: z.boolean(),
    band_count: z.int(),
    base: zBaseQuantity,
    clause_number: z.string(),
    clause_title: z.string().nullable(),
    clause_uid: z.string(),
    comparator: zComparator,
    confidence: z.number(),
    curve_count: z.int(),
    extraction_method: zExtractionMethod,
    group_id: z.string().nullable(),
    item_ref: z.string().nullable(),
    local_index: z.int(),
    modality: zModality,
    obligated_party: z.string().nullable(),
    open_parameter_count: z.int(),
    parameter_id: z.string().nullable(),
    parameter_name: z.string().nullable(),
    quality_flags: z.array(z.string()),
    reference_point: zReferencePoint,
    req_uid: z.string(),
    section_code: z.string(),
    source_page: z.int(),
    statement: z.string(),
    time_limit_s: z.number().nullable(),
    unit: z.string().nullable(),
    value: z.number().nullable(),
    value_max: z.number().nullable(),
    value_min: z.number().nullable(),
    value_text: z.string(),
    value_type: zValueType,
    verification_methods: z.array(zVerificationMethod)
});

/**
 * Page[RequirementRow]
 */
export const zPageRequirementRow = z.object({
    items: z.array(zRequirementRow),
    limit: z.int(),
    next_cursor: z.string().nullable(),
    total: z.int()
});

/**
 * ParameterDetail
 */
export const zParameterDetail = z.object({
    group: zParameterGroup.nullable(),
    parameter: zParameter,
    replaced_by: zParameterRow.nullable(),
    replacement_chain: z.array(zParameterRow),
    replaces: z.array(zParameterRow),
    requirement_total: z.int(),
    requirements: z.array(zRequirementRow),
    synonyms: z.array(zSynonym)
});

/**
 * VersionBuild
 */
export const zVersionBuild = z.object({
    job: zJobBrief.nullable(),
    last_run: zRunBrief.nullable(),
    stale: z.boolean(),
    stale_fields: z.array(z.string()),
    state: z.enum([
        'not_built',
        'queued',
        'building',
        'interrupted',
        'failed',
        'ready',
        'deleting'
    ])
});

/**
 * DatasetRow
 */
export const zDatasetRow = z.object({
    adapter: z.string(),
    build: zVersionBuild,
    clauses: z.int().nullable(),
    doc_uid: z.string(),
    family_id: z.string(),
    family_title: z.string(),
    in_effect: z.boolean(),
    jurisdictions: z.array(z.string()),
    language: z.string(),
    licence_class: zLicenceClass,
    lifecycle_status: zDocumentStatus,
    page_count: z.int().nullable(),
    publisher: z.string(),
    quality_score: z.number().gte(0).lte(100).nullable(),
    requirements: z.int().nullable(),
    updating_run: z.string().nullable(),
    version_label: z.string()
});

/**
 * Page[DatasetRow]
 */
export const zPageDatasetRow = z.object({
    items: z.array(zDatasetRow),
    limit: z.int(),
    next_cursor: z.string().nullable(),
    total: z.int()
});

/**
 * VersionDiff
 */
export const zVersionDiff = z.object({
    changed: z.array(z.string()),
    changed_context: z.array(z.string()),
    changed_text: z.array(z.string()),
    new: z.array(z.string()),
    new_doc_uid: z.string(),
    old_doc_uid: z.string(),
    removed: z.array(z.string()),
    renumbered: z.array(zRenumberedClause),
    unchanged: z.array(z.string())
});

/**
 * VersionDiffOut
 */
export const zVersionDiffOut = z.object({
    counts: zDiffCounts,
    diff: zVersionDiff,
    section_check: zSectionChangeCheck
});

/**
 * VersionGraphState
 */
export const zVersionGraphState = z.object({
    build_state: zBuildState.nullable(),
    content_revision: z.int().nullable(),
    document_exists: z.boolean(),
    in_effect: z.boolean(),
    lifecycle_status: zDocumentStatus.nullable(),
    ready_at: z.iso.datetime({ offset: true }).nullable(),
    updating_run: z.string().nullable(),
    valid_from: z.iso.date().nullable(),
    valid_to: z.iso.date().nullable()
});

/**
 * VersionEntry
 */
export const zVersionEntry = z.object({
    build: zVersionBuild,
    doc_uid: z.string(),
    family_id: z.string(),
    graph: zVersionGraphState,
    layout_profile: zLayoutProfileRef.nullable(),
    meta: zEntryMeta,
    source_file: zSourceFileOut.nullable(),
    version: zSourceVersion
});

/**
 * FamilyDetail
 */
export const zFamilyDetail = z.object({
    effective_sections: z.array(zEffectiveSection),
    family: zSourceFamilyCore,
    language_support: zLanguageSupport,
    meta: zEntryMeta,
    versions: z.array(zVersionEntry)
});

/**
 * FamilySummary
 */
export const zFamilySummary = z.object({
    building: z.int().gte(0),
    current: zVersionEntry.nullable(),
    family: zSourceFamilyCore,
    language_support: zLanguageSupport,
    meta: zEntryMeta,
    ready: z.int().gte(0),
    stale: z.int().gte(0),
    versions: z.int().gte(0)
});

/**
 * Page[FamilySummary]
 */
export const zPageFamilySummary = z.object({
    items: z.array(zFamilySummary),
    limit: z.int(),
    next_cursor: z.string().nullable(),
    total: z.int()
});

/**
 * VersionImpact
 */
export const zVersionImpact = z.object({
    compared: zImpactVersion.nullable(),
    topics: z.array(zTopicImpact)
});

/**
 * VersionIn
 */
export const zVersionIn = z.object({
    published_on: z.iso.date().nullish(),
    retrieved_on: z.iso.date().nullish(),
    source_sha256: z.string().regex(/^[0-9a-f]{64}$/),
    source_url: z.url().min(1).max(2083).nullish(),
    status: zDocumentStatus.optional().default('draft'),
    valid_from: z.iso.date().nullish(),
    valid_to: z.iso.date().nullish(),
    version_label: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._-]{0,31}$/)
});

/**
 * FamilyIn
 */
export const zFamilyIn = z.object({
    adapter: z.enum(['generic', 'neso_gb']).optional().default('generic'),
    default_scope: zSectionDefaults.optional(),
    doc_type: zDocType,
    family_id: z.string().max(32).regex(/^[A-Z][A-Z0-9]*(-[A-Z0-9]+)*$/),
    first_version: zVersionIn.nullish(),
    implements: z.array(z.string()).optional(),
    jurisdictions: z.array(z.string().max(32).regex(/^[A-Z][A-Z0-9]*(-[A-Z0-9]+)*$/)).min(1),
    landing_page: z.url().min(1).max(2083).nullish(),
    language: z.string(),
    licence_class: zLicenceClass,
    publisher: z.string().max(32).regex(/^[A-Z][A-Z0-9]*(-[A-Z0-9]+)*$/),
    publisher_hints: z.string().max(4000).nullish(),
    region_primary: z.boolean().optional().default(false),
    sections: z.array(zSectionScopeIn).max(500).optional(),
    title: z.string().min(1).max(300)
});

/**
 * VersionPatch
 */
export const zVersionPatch = z.object({
    published_on: z.iso.date().nullish(),
    retrieved_on: z.iso.date().nullish(),
    source_sha256: z.string().regex(/^[0-9a-f]{64}$/).nullish(),
    source_url: z.url().min(1).max(2083).nullish(),
    status: zDocumentStatus.nullish(),
    valid_from: z.iso.date().nullish(),
    valid_to: z.iso.date().nullish()
});

/**
 * VersionSummary
 */
export const zVersionSummary = z.object({
    doc_uid: z.string(),
    page_count: z.int().nullable(),
    status: zDocumentStatus,
    title: z.string(),
    valid_from: z.iso.date().nullable(),
    valid_to: z.iso.date().nullable(),
    version_label: z.string()
});

/**
 * FamilyOut
 */
export const zFamilyOut = z.object({
    doc_type: zDocType,
    family_id: z.string(),
    jurisdictions: z.array(z.string()),
    landing_page: z.string().nullable(),
    language: z.string(),
    licence_class: zLicenceClass,
    publisher: z.string(),
    publisher_name: z.string().nullable(),
    sections: z.array(zSectionScopeOut),
    title: z.string(),
    versions: z.array(zVersionSummary)
});

/**
 * WeakPasswordReason
 */
export const zWeakPasswordReason = z.enum([
    'too_short',
    'too_long',
    'contains_email',
    'breached'
]);

/**
 * WhatsNewAudience
 */
export const zWhatsNewAudience = z.enum(['everyone', 'admins']);

/**
 * WhatsNewPage
 */
export const zWhatsNewPage = z.enum([
    'overview',
    'assistant',
    'workspace',
    'library',
    'curves',
    'glossary',
    'versions',
    'assessments',
    'quality',
    'parameters',
    'runs',
    'sources',
    'users',
    'feedback',
    'settings',
    'datasets',
    'system'
]);

/**
 * WhatsNewEntry
 */
export const zWhatsNewEntry = z.object({
    audience: zWhatsNewAudience,
    id: z.int().gte(1),
    page: zWhatsNewPage.nullable(),
    released: z.iso.date(),
    text: z.string().max(400),
    title: z.string().max(80),
    unseen: z.boolean()
});

/**
 * WhatsNew
 */
export const zWhatsNew = z.object({
    entries: z.array(zWhatsNewEntry),
    seen_through: z.int().gte(0),
    unseen: z.int().gte(0)
});

/**
 * WhatsNewSeen
 */
export const zWhatsNewSeen = z.object({
    through: z.int().gte(0).lte(1000000)
});

/**
 * WorkerOut
 */
export const zWorkerOut = z.object({
    alive: z.boolean(),
    host: z.string(),
    pools: z.array(z.string()),
    seen_at: z.iso.datetime({ offset: true }),
    worker_id: z.string()
});

/**
 * JobCapacity
 */
export const zJobCapacity = z.object({
    graph_writers: zPoolCapacity,
    launcher: z.enum([
        'local',
        'worker',
        'aca',
        'none'
    ]),
    month_to_date_usd: z.number().gte(0),
    pools: z.array(zPoolCapacity),
    run_cost_cap: zCostCap,
    workers: z.array(zWorkerOut)
});

export const zOperationsListActivityQuery = z.object({
    limit: z.int().gte(1).lte(100).optional().default(20)
});

/**
 * Response Operations List Activity
 *
 * Successful Response
 */
export const zOperationsListActivityResponse = z.array(zActivityItem);

export const zAdminListAuthEventsQuery = z.object({
    type: zAuthEventType.nullish(),
    email: z.string().max(254).nullish(),
    since: z.iso.datetime({ offset: true }).nullish(),
    cursor: z.string().max(4096).nullish(),
    limit: z.int().gte(1).nullish()
});

/**
 * Successful Response
 */
export const zAdminListAuthEventsResponse = zPageAuthEvent;

export const zDatasetsListDatasetsQuery = z.object({
    q: z.string().max(200).nullish(),
    jurisdiction: z.array(z.string()).nullish(),
    language: z.array(z.string()).nullish(),
    build: z.array(z.enum([
        'not_built',
        'queued',
        'building',
        'interrupted',
        'failed',
        'ready',
        'deleting'
    ])).nullish(),
    active: z.boolean().nullish(),
    cursor: z.string().max(4096).nullish(),
    limit: z.int().gte(1).nullish()
});

/**
 * Successful Response
 */
export const zDatasetsListDatasetsResponse = zPageDatasetRow;

/**
 * Successful Response
 */
export const zDatasetsGetDatasetsOverviewResponse = zDatasetsOverview;

export const zDatasetsGetDocumentContentsPath = z.object({
    doc_uid: z.string()
});

/**
 * Successful Response
 */
export const zDatasetsGetDocumentContentsResponse = zDocumentContents;

export const zDatasetsGetDatasetPreviewPath = z.object({
    doc_uid: z.string()
});

/**
 * Successful Response
 */
export const zDatasetsGetDatasetPreviewResponse = zPreviewReport;

export const zAdminListFeedbackQuery = z.object({
    status: zFeedbackStatus.nullish(),
    kind: zFeedbackKind.nullish(),
    cursor: z.string().max(4096).nullish(),
    limit: z.int().gte(1).nullish()
});

/**
 * Successful Response
 */
export const zAdminListFeedbackResponse = zPageFeedbackReport;

/**
 * Successful Response
 */
export const zAdminFeedbackSummaryResponse = zFeedbackSummary;

export const zAdminDeleteFeedbackPath = z.object({
    report_id: z.string().max(64)
});

/**
 * Successful Response
 */
export const zAdminDeleteFeedbackResponse = z.void();

export const zAdminGetFeedbackPath = z.object({
    report_id: z.string().max(64)
});

/**
 * Successful Response
 */
export const zAdminGetFeedbackResponse = zFeedbackReportDetail;

export const zAdminUpdateFeedbackBody = zFeedbackUpdate;

export const zAdminUpdateFeedbackPath = z.object({
    report_id: z.string().max(64)
});

/**
 * Successful Response
 */
export const zAdminUpdateFeedbackResponse = zFeedbackReportDetail;

export const zAdminGetFeedbackScreenshotPath = z.object({
    report_id: z.string().max(64)
});

/**
 * The report's screenshot as the sandbox re-encoded it.
 */
export const zAdminGetFeedbackScreenshotResponse = z.string();

export const zAdminListInvitationsQuery = z.object({
    status: zInvitationStatus.nullish(),
    cursor: z.string().max(4096).nullish(),
    limit: z.int().gte(1).nullish()
});

/**
 * Successful Response
 */
export const zAdminListInvitationsResponse = zPageInvitation;

export const zAdminCreateInvitationBody = zInvitationCreate;

/**
 * Successful Response
 */
export const zAdminCreateInvitationResponse = zInvitationIssued;

export const zAdminRevokeInvitationPath = z.object({
    invitation_id: z.string()
});

/**
 * Successful Response
 */
export const zAdminRevokeInvitationResponse = z.void();

export const zAdminResendInvitationPath = z.object({
    invitation_id: z.string()
});

/**
 * Successful Response
 */
export const zAdminResendInvitationResponse = zInvitationIssued;

/**
 * Successful Response
 */
export const zRegistryGetRegistrySummaryResponse = zRegistrySummary;

/**
 * Response Registry List Source Adapters
 *
 * Successful Response
 */
export const zRegistryListSourceAdaptersResponse = z.array(zAdapterInfo);

export const zRegistryListRegistryAuthoritiesQuery = z.object({
    q: z.string().max(200).nullish(),
    include_retired: z.boolean().optional().default(false),
    cursor: z.string().max(4096).nullish(),
    limit: z.int().gte(1).nullish()
});

/**
 * Successful Response
 */
export const zRegistryListRegistryAuthoritiesResponse = zPageAuthorityEntry;

export const zRegistryCreateRegistryAuthorityBody = zAuthorityIn;

/**
 * Successful Response
 */
export const zRegistryCreateRegistryAuthorityResponse = zAuthorityEntry;

export const zRegistryRetireRegistryAuthorityHeaders = z.object({
    'If-Match': z.string().nullish()
});

export const zRegistryRetireRegistryAuthorityPath = z.object({
    authority_id: z.string()
});

/**
 * Successful Response
 */
export const zRegistryRetireRegistryAuthorityResponse = z.void();

export const zRegistryGetRegistryAuthorityPath = z.object({
    authority_id: z.string()
});

/**
 * Successful Response
 */
export const zRegistryGetRegistryAuthorityResponse = zAuthorityEntry;

export const zRegistryUpdateRegistryAuthorityBody = zAuthorityPatch;

export const zRegistryUpdateRegistryAuthorityHeaders = z.object({
    'If-Match': z.string().nullish()
});

export const zRegistryUpdateRegistryAuthorityPath = z.object({
    authority_id: z.string()
});

/**
 * Successful Response
 */
export const zRegistryUpdateRegistryAuthorityResponse = zAuthorityEntry;

export const zRegistryListRegistryRevisionsPath = z.object({
    entry_key: z.string()
});

export const zRegistryListRegistryRevisionsQuery = z.object({
    cursor: z.string().max(4096).nullish(),
    limit: z.int().gte(1).nullish()
});

/**
 * Successful Response
 */
export const zRegistryListRegistryRevisionsResponse = zPageEntryRevision;

export const zRegistryListDatasetEventsQuery = z.object({
    entity_kind: z.string().nullish(),
    entity_id: z.string().nullish(),
    family_id: z.string().nullish(),
    action: z.array(zDatasetAction).nullish(),
    actor_key: z.string().nullish(),
    since: z.iso.datetime({ offset: true }).nullish(),
    until: z.iso.datetime({ offset: true }).nullish(),
    cursor: z.string().max(4096).nullish(),
    limit: z.int().gte(1).nullish()
});

/**
 * Successful Response
 */
export const zRegistryListDatasetEventsResponse = zPageDatasetEventOut;

/**
 * Successful Response
 */
export const zRegistryVerifyDatasetEventsResponse = zAuditVerification;

/**
 * The registry in the sources.yaml format, as attachment sources.r<revision>.yaml.
 */
export const zRegistryExportSourceRegistryResponse = z.string();

export const zRegistryListRegistryFamiliesQuery = z.object({
    q: z.string().max(200).nullish(),
    jurisdiction: z.array(z.string()).nullish(),
    language: z.array(z.string()).nullish(),
    adapter: z.string().nullish(),
    include_retired: z.boolean().optional().default(false),
    cursor: z.string().max(4096).nullish(),
    limit: z.int().gte(1).nullish()
});

/**
 * Successful Response
 */
export const zRegistryListRegistryFamiliesResponse = zPageFamilySummary;

export const zRegistryCreateRegistryFamilyBody = zFamilyIn;

/**
 * Successful Response
 */
export const zRegistryCreateRegistryFamilyResponse = zFamilyDetail;

export const zRegistryRetireRegistryFamilyHeaders = z.object({
    'If-Match': z.string().nullish()
});

export const zRegistryRetireRegistryFamilyPath = z.object({
    family_id: z.string()
});

/**
 * Successful Response
 */
export const zRegistryRetireRegistryFamilyResponse = z.void();

export const zRegistryGetRegistryFamilyPath = z.object({
    family_id: z.string()
});

/**
 * Successful Response
 */
export const zRegistryGetRegistryFamilyResponse = zFamilyDetail;

export const zRegistryUpdateRegistryFamilyBody = zFamilyPatch;

export const zRegistryUpdateRegistryFamilyHeaders = z.object({
    'If-Match': z.string().nullish()
});

export const zRegistryUpdateRegistryFamilyPath = z.object({
    family_id: z.string()
});

/**
 * Successful Response
 */
export const zRegistryUpdateRegistryFamilyResponse = zFamilyDetail;

export const zRegistryReplaceFamilySectionsBody = zSectionsIn;

export const zRegistryReplaceFamilySectionsHeaders = z.object({
    'If-Match': z.string().nullish()
});

export const zRegistryReplaceFamilySectionsPath = z.object({
    family_id: z.string()
});

/**
 * Successful Response
 */
export const zRegistryReplaceFamilySectionsResponse = zFamilyDetail;

export const zRegistryCreateRegistryVersionBody = zVersionIn;

export const zRegistryCreateRegistryVersionPath = z.object({
    family_id: z.string()
});

/**
 * Successful Response
 */
export const zRegistryCreateRegistryVersionResponse = zVersionEntry;

export const zRegistryRetireRegistryVersionHeaders = z.object({
    'If-Match': z.string().nullish()
});

export const zRegistryRetireRegistryVersionPath = z.object({
    family_id: z.string(),
    version_label: z.string()
});

/**
 * Successful Response
 */
export const zRegistryRetireRegistryVersionResponse = z.void();

export const zRegistryGetRegistryVersionPath = z.object({
    family_id: z.string(),
    version_label: z.string()
});

/**
 * Successful Response
 */
export const zRegistryGetRegistryVersionResponse = zVersionEntry;

export const zRegistryUpdateRegistryVersionBody = zVersionPatch;

export const zRegistryUpdateRegistryVersionHeaders = z.object({
    'If-Match': z.string().nullish()
});

export const zRegistryUpdateRegistryVersionPath = z.object({
    family_id: z.string(),
    version_label: z.string()
});

/**
 * Successful Response
 */
export const zRegistryUpdateRegistryVersionResponse = zVersionEntry;

export const zRegistryListRegistryJurisdictionsQuery = z.object({
    q: z.string().max(200).nullish(),
    include_retired: z.boolean().optional().default(false),
    cursor: z.string().max(4096).nullish(),
    limit: z.int().gte(1).nullish()
});

/**
 * Successful Response
 */
export const zRegistryListRegistryJurisdictionsResponse = zPageJurisdictionEntry;

export const zRegistryCreateRegistryJurisdictionBody = zJurisdictionIn;

/**
 * Successful Response
 */
export const zRegistryCreateRegistryJurisdictionResponse = zJurisdictionEntry;

export const zRegistryRetireRegistryJurisdictionHeaders = z.object({
    'If-Match': z.string().nullish()
});

export const zRegistryRetireRegistryJurisdictionPath = z.object({
    jurisdiction_id: z.string()
});

/**
 * Successful Response
 */
export const zRegistryRetireRegistryJurisdictionResponse = z.void();

export const zRegistryGetRegistryJurisdictionPath = z.object({
    jurisdiction_id: z.string()
});

/**
 * Successful Response
 */
export const zRegistryGetRegistryJurisdictionResponse = zJurisdictionEntry;

export const zRegistryUpdateRegistryJurisdictionBody = zJurisdictionPatch;

export const zRegistryUpdateRegistryJurisdictionHeaders = z.object({
    'If-Match': z.string().nullish()
});

export const zRegistryUpdateRegistryJurisdictionPath = z.object({
    jurisdiction_id: z.string()
});

/**
 * Successful Response
 */
export const zRegistryUpdateRegistryJurisdictionResponse = zJurisdictionEntry;

/**
 * Response Registry List Supported Languages
 *
 * Successful Response
 */
export const zRegistryListSupportedLanguagesResponse = z.array(zLanguageSupport);

/**
 * Successful Response
 */
export const zAdminGetSettingsResponse = zSettingsPage;

export const zAdminUpdateSettingsBody = zSettingsUpdate;

export const zAdminUpdateSettingsHeaders = z.object({
    'If-Match': z.string().nullish()
});

/**
 * Successful Response
 */
export const zAdminUpdateSettingsResponse = zSettingsPage;

export const zAdminListSettingsHistoryQuery = z.object({
    limit: z.int().gte(1).lte(200).optional().default(50)
});

/**
 * Response Admin List Settings History
 *
 * Successful Response
 */
export const zAdminListSettingsHistoryResponse = z.array(zSettingsHistoryEntry);

export const zUploadsListSourceFilesQuery = z.object({
    q: z.string().max(200).nullish(),
    unregistered: z.boolean().nullish(),
    cursor: z.string().max(4096).nullish(),
    limit: z.int().gte(1).nullish()
});

/**
 * Successful Response
 */
export const zUploadsListSourceFilesResponse = zPageSourceFileOut;

export const zUploadsGetSourceFilePath = z.object({
    sha256: z.string().regex(/^[0-9a-f]{64}$/)
});

/**
 * Successful Response
 */
export const zUploadsGetSourceFileResponse = zSourceFileOut;

export const zUploadsGetSourceFilePdfHeaders = z.object({
    Range: z.string().nullish(),
    'If-None-Match': z.string().nullish()
});

export const zUploadsGetSourceFilePdfPath = z.object({
    sha256: z.string().regex(/^[0-9a-f]{64}$/)
});

/**
 * The stored PDF; 206 with Content-Range for a Range request.
 */
export const zUploadsGetSourceFilePdfResponse = z.string();

export const zUploadsGetSourceProfilePath = z.object({
    sha256: z.string().regex(/^[0-9a-f]{64}$/)
});

export const zUploadsGetSourceProfileQuery = z.object({
    adapter: z.string().nullish()
});

/**
 * Successful Response
 */
export const zUploadsGetSourceProfileResponse = zSourceProfileOut;

export const zUploadsCreateUploadBody = zUploadCreate;

/**
 * Successful Response
 */
export const zUploadsCreateUploadResponse = zUploadSession;

export const zUploadsDeleteUploadPath = z.object({
    upload_id: z.string()
});

/**
 * Successful Response
 */
export const zUploadsDeleteUploadResponse = z.void();

export const zUploadsGetUploadPath = z.object({
    upload_id: z.string()
});

/**
 * Successful Response
 */
export const zUploadsGetUploadResponse = zUploadSession;

export const zUploadsCompleteUploadBody = zUploadComplete;

export const zUploadsCompleteUploadPath = z.object({
    upload_id: z.string()
});

/**
 * Successful Response
 */
export const zUploadsCompleteUploadResponse = zUploadResult;

export const zUploadsPutUploadPartBody = z.string();

export const zUploadsPutUploadPartHeaders = z.object({
    'Content-Digest': z.string().nullish()
});

export const zUploadsPutUploadPartPath = z.object({
    upload_id: z.string(),
    part_number: z.int().gte(1).lte(10000)
});

/**
 * Successful Response
 */
export const zUploadsPutUploadPartResponse = zUploadPart;

export const zAdminListUsersQuery = z.object({
    q: z.string().max(254).nullish(),
    status: zUserStatus.nullish(),
    role: zRole.nullish(),
    cursor: z.string().max(4096).nullish(),
    limit: z.int().gte(1).nullish()
});

/**
 * Successful Response
 */
export const zAdminListUsersResponse = zPageUserAccount;

export const zAdminGetUserPath = z.object({
    user_id: z.string()
});

/**
 * Successful Response
 */
export const zAdminGetUserResponse = zUserAccount;

export const zAdminUpdateUserBody = zUserUpdate;

export const zAdminUpdateUserPath = z.object({
    user_id: z.string()
});

/**
 * Successful Response
 */
export const zAdminUpdateUserResponse = zUserAccount;

export const zAdminSetAssistantBudgetBody = zAssistantBudgetUpdate;

export const zAdminSetAssistantBudgetPath = z.object({
    user_id: z.string()
});

/**
 * Successful Response
 */
export const zAdminSetAssistantBudgetResponse = zUserAccount;

export const zAdminResetUserMfaPath = z.object({
    user_id: z.string()
});

/**
 * Successful Response
 */
export const zAdminResetUserMfaResponse = z.void();

export const zAdminSendPasswordResetPath = z.object({
    user_id: z.string()
});

/**
 * A password reset link is issued and, when email is configured, emailed to the user.
 */
export const zAdminSendPasswordResetResponse = zPasswordResetSent;

export const zAdminRevokeUserSessionsPath = z.object({
    user_id: z.string()
});

/**
 * Successful Response
 */
export const zAdminRevokeUserSessionsResponse = z.void();

export const zAssessmentsListAssessmentsQuery = z.object({
    jurisdiction: z.string().min(1).max(40).nullish(),
    status: z.array(zAssessmentStatus).nullish()
});

/**
 * Response Assessments List Assessments
 *
 * Successful Response
 */
export const zAssessmentsListAssessmentsResponse = z.array(zAssessmentSummary);

export const zAssessmentsCreateAssessmentBody = zAssessmentCreate;

export const zAssessmentsCreateAssessmentHeaders = z.object({
    'Idempotency-Key': z.uuid().nullish()
});

/**
 * The assessment created earlier with the same Idempotency-Key.
 */
export const zAssessmentsCreateAssessmentResponse = zAssessmentDetail;

export const zAssessmentsEstimateAssessmentQuery = z.object({
    capability_doc_uid: z.string().regex(/^[0-9a-f]{64}$/),
    product_types: z.array(z.string()).nullish()
});

/**
 * Successful Response
 */
export const zAssessmentsEstimateAssessmentResponse = zAssessmentEstimate;

export const zAssessmentsDeleteAssessmentPath = z.object({
    assessment_uid: z.string().regex(/^ASM-\d{8}-[0-9a-f]{6}$/)
});

/**
 * Successful Response
 */
export const zAssessmentsDeleteAssessmentResponse = z.void();

export const zAssessmentsGetAssessmentPath = z.object({
    assessment_uid: z.string().regex(/^ASM-\d{8}-[0-9a-f]{6}$/)
});

/**
 * Successful Response
 */
export const zAssessmentsGetAssessmentResponse = zAssessmentDetail;

export const zAssessmentsApproveAssessmentBody = zAssessmentApproval;

export const zAssessmentsApproveAssessmentPath = z.object({
    assessment_uid: z.string().regex(/^ASM-\d{8}-[0-9a-f]{6}$/)
});

/**
 * Successful Response
 */
export const zAssessmentsApproveAssessmentResponse = zAssessmentDetail;

export const zAssessmentsExportAssessmentXlsxPath = z.object({
    assessment_uid: z.string().regex(/^ASM-\d{8}-[0-9a-f]{6}$/)
});

/**
 * Assessment workbook (sheets Assessment, Results and Capabilities).
 */
export const zAssessmentsExportAssessmentXlsxResponse = z.string();

export const zAssessmentsReopenAssessmentBody = zAssessmentReopen;

export const zAssessmentsReopenAssessmentPath = z.object({
    assessment_uid: z.string().regex(/^ASM-\d{8}-[0-9a-f]{6}$/)
});

/**
 * Successful Response
 */
export const zAssessmentsReopenAssessmentResponse = zAssessmentDetail;

export const zAssessmentsRerunAssessmentBody = zAssessmentRerun;

export const zAssessmentsRerunAssessmentHeaders = z.object({
    'Idempotency-Key': z.uuid().nullish()
});

export const zAssessmentsRerunAssessmentPath = z.object({
    assessment_uid: z.string().regex(/^ASM-\d{8}-[0-9a-f]{6}$/)
});

/**
 * The job started earlier with the same Idempotency-Key.
 */
export const zAssessmentsRerunAssessmentResponse = zOperationJob;

export const zAssessmentsListTopicResultsPath = z.object({
    assessment_uid: z.string().regex(/^ASM-\d{8}-[0-9a-f]{6}$/)
});

/**
 * Response Assessments List Topic Results
 *
 * Successful Response
 */
export const zAssessmentsListTopicResultsResponse = z.array(zTopicResultRow);

export const zAssessmentsGetTopicResultPath = z.object({
    topic_key: z.string().max(64).regex(/^[a-z][a-z0-9_]*$/),
    assessment_uid: z.string().regex(/^ASM-\d{8}-[0-9a-f]{6}$/)
});

/**
 * The topic result.
 */
export const zAssessmentsGetTopicResultResponse = zTopicResultDetail;

export const zAssessmentsReviewTopicResultBody = zTopicReview;

export const zAssessmentsReviewTopicResultHeaders = z.object({
    'If-Match': z.string().nullish()
});

export const zAssessmentsReviewTopicResultPath = z.object({
    topic_key: z.string().max(64).regex(/^[a-z][a-z0-9_]*$/),
    assessment_uid: z.string().regex(/^ASM-\d{8}-[0-9a-f]{6}$/)
});

/**
 * The reviewed topic with its new revision.
 */
export const zAssessmentsReviewTopicResultResponse = zTopicResultDetail;

export const zAssessmentsGetVersionImpactPath = z.object({
    assessment_uid: z.string().regex(/^ASM-\d{8}-[0-9a-f]{6}$/)
});

/**
 * Successful Response
 */
export const zAssessmentsGetVersionImpactResponse = zVersionImpact;

export const zAssistantCreateAnswerBody = zAnswerRequest;

/**
 * Successful Response
 */
export const zAssistantGetBudgetResponse = zAssistantBudgetOut;

export const zAssistantListSuggestionsQuery = z.object({
    doc_uid: z.string().nullish(),
    req_uid: z.string().nullish()
});

/**
 * Response Assistant List Suggestions
 *
 * Successful Response
 */
export const zAssistantListSuggestionsResponse = z.array(zSuggestion);

/**
 * Successful Response
 */
export const zAssistantDeleteThreadsResponse = z.void();

export const zAssistantListThreadsQuery = z.object({
    q: z.string().max(200).nullish(),
    cursor: z.string().max(4096).nullish(),
    limit: z.int().gte(1).nullish()
});

/**
 * Successful Response
 */
export const zAssistantListThreadsResponse = zPageAssistantThread;

export const zAssistantDeleteThreadPath = z.object({
    thread_id: z.string().min(1).max(64)
});

/**
 * Successful Response
 */
export const zAssistantDeleteThreadResponse = z.void();

export const zAssistantGetThreadPath = z.object({
    thread_id: z.string().min(1).max(64)
});

/**
 * Successful Response
 */
export const zAssistantGetThreadResponse = zAssistantThreadDetail;

export const zAssistantRenameThreadBody = zThreadRename;

export const zAssistantRenameThreadPath = z.object({
    thread_id: z.string().min(1).max(64)
});

/**
 * Successful Response
 */
export const zAssistantRenameThreadResponse = zAssistantThread;

export const zAuthAcceptInvitationBody = zInvitationAcceptance;

/**
 * Account created; the challenge cookie of the authenticator setup is set.
 */
export const zAuthAcceptInvitationResponse = zSignInStep;

export const zAuthLookUpInvitationBody = zInvitationLookupRequest;

/**
 * Successful Response
 */
export const zAuthLookUpInvitationResponse = zInvitationPreview;

export const zAuthStartAuthenticatorReplacementBody = zAuthenticatorReplacement;

/**
 * The new authenticator's key; the challenge cookie of its setup is set.
 */
export const zAuthStartAuthenticatorReplacementResponse = zTotpSetup;

export const zAuthConfirmAuthenticatorReplacementBody = zCodeConfirmation;

/**
 * New authenticator set up; other sessions revoked, challenge cookie cleared.
 */
export const zAuthConfirmAuthenticatorReplacementResponse = z.void();

/**
 * Successful Response
 */
export const zAuthGetAuthOptionsResponse = zAuthOptions;

export const zAuthChangePasswordBody = zPasswordChange;

/**
 * Successful Response
 */
export const zAuthChangePasswordResponse = z.void();

/**
 * Successful Response
 */
export const zAuthGetPasswordPolicyResponse = zPasswordPolicy;

export const zAuthRequestPasswordResetBody = zPasswordResetRequest;

export const zAuthConfirmPasswordResetBody = zPasswordResetConfirmation;

/**
 * Password reset and every session of the user revoked; the challenge cookie of the next step is set.
 */
export const zAuthConfirmPasswordResetResponse = zSignInStep;

export const zAuthLookUpPasswordResetBody = zPasswordResetLookupRequest;

/**
 * Successful Response
 */
export const zAuthLookUpPasswordResetResponse = zPasswordResetPreview;

/**
 * Successful Response
 */
export const zAuthRevokeOtherSessionsResponse = z.void();

/**
 * Response Auth List Sessions
 *
 * Successful Response
 */
export const zAuthListSessionsResponse = z.array(zActiveSession);

export const zAuthRevokeSessionPath = z.object({
    session_id: z.string()
});

/**
 * Successful Response
 */
export const zAuthRevokeSessionResponse = z.void();

export const zAuthSignInBody = zSignInRequest;

/**
 * Password accepted; the challenge cookie of the next step is set.
 */
export const zAuthSignInResponse = zSignInStep;

export const zAuthCompleteSignInBody = zCodeConfirmation;

/**
 * Signed in; the challenge cookie is cleared and a new session cookie is set.
 */
export const zAuthCompleteSignInResponse = zMe;

/**
 * Successful Response
 */
export const zAuthStartSetupResponse = zTotpSetup;

export const zAuthConfirmSetupBody = zCodeConfirmation;

/**
 * Set up and signed in; the challenge cookie is cleared and a session cookie set.
 */
export const zAuthConfirmSetupResponse = zMe;

/**
 * Signed out (also without a valid session); the session cookie is cleared.
 */
export const zAuthSignOutResponse = z.void();

/**
 * Response Catalog List Authorities
 *
 * Successful Response
 */
export const zCatalogListAuthoritiesResponse = z.array(zAuthorityOut);

export const zCurvesListBandsQuery = z.object({
    quantity_kind: z.array(zQuantityKind).nullish(),
    doc_uid: z.string(),
    cursor: z.string().max(4096).nullish(),
    limit: z.int().gte(1).nullish()
});

/**
 * Successful Response
 */
export const zCurvesListBandsResponse = zPageBandRow;

/**
 * Response Capabilities List Capability Documents
 *
 * Successful Response
 */
export const zCapabilitiesListCapabilityDocumentsResponse = z.array(zCapabilityDocumentSummary);

export const zCapabilitiesCreateCapabilityUploadBody = zUploadCreate;

/**
 * Successful Response
 */
export const zCapabilitiesCreateCapabilityUploadResponse = zCapabilityUploadSession;

export const zCapabilitiesDeleteCapabilityUploadPath = z.object({
    upload_id: z.string()
});

/**
 * Successful Response
 */
export const zCapabilitiesDeleteCapabilityUploadResponse = z.void();

export const zCapabilitiesGetCapabilityUploadPath = z.object({
    upload_id: z.string()
});

/**
 * Successful Response
 */
export const zCapabilitiesGetCapabilityUploadResponse = zCapabilityUploadSession;

export const zCapabilitiesCompleteCapabilityUploadPath = z.object({
    upload_id: z.string()
});

/**
 * Successful Response
 */
export const zCapabilitiesCompleteCapabilityUploadResponse = zCapabilityUploadResult;

export const zCapabilitiesPutCapabilityUploadPartBody = z.string();

export const zCapabilitiesPutCapabilityUploadPartHeaders = z.object({
    'Content-Digest': z.string().nullish()
});

export const zCapabilitiesPutCapabilityUploadPartPath = z.object({
    upload_id: z.string(),
    part_number: z.int().gte(1).lte(10000)
});

/**
 * Successful Response
 */
export const zCapabilitiesPutCapabilityUploadPartResponse = zUploadPart;

export const zCapabilitiesDeleteCapabilityDocumentPath = z.object({
    capability_doc_uid: z.string().regex(/^[0-9a-f]{64}$/)
});

/**
 * Successful Response
 */
export const zCapabilitiesDeleteCapabilityDocumentResponse = z.void();

export const zCapabilitiesGetCapabilityDocumentPath = z.object({
    capability_doc_uid: z.string().regex(/^[0-9a-f]{64}$/)
});

/**
 * Successful Response
 */
export const zCapabilitiesGetCapabilityDocumentResponse = zCapabilityDocumentDetail;

export const zCapabilitiesListCapabilitiesPath = z.object({
    capability_doc_uid: z.string().regex(/^[0-9a-f]{64}$/)
});

/**
 * Response Capabilities List Capabilities
 *
 * Successful Response
 */
export const zCapabilitiesListCapabilitiesResponse = z.array(zCapabilityOut);

export const zCapabilitiesExtractCapabilityDocumentHeaders = z.object({
    'Idempotency-Key': z.uuid().nullish()
});

export const zCapabilitiesExtractCapabilityDocumentPath = z.object({
    capability_doc_uid: z.string().regex(/^[0-9a-f]{64}$/)
});

/**
 * Successful Response
 */
export const zCapabilitiesExtractCapabilityDocumentResponse = zCapabilityDocumentDetail;

export const zCapabilitiesGetCapabilityDocumentPdfHeaders = z.object({
    Range: z.string().nullish(),
    'If-None-Match': z.string().nullish(),
    'If-Range': z.string().nullish()
});

export const zCapabilitiesGetCapabilityDocumentPdfPath = z.object({
    capability_doc_uid: z.string().regex(/^[0-9a-f]{64}$/)
});

/**
 * The original PDF; 206 with Content-Range for a Range request.
 */
export const zCapabilitiesGetCapabilityDocumentPdfResponse = z.string();

export const zClausesListClausesQuery = z.object({
    q: z.string().min(1).max(500).nullish(),
    number_prefix: z.string().nullish(),
    section: z.string().nullish(),
    kind: z.array(zClauseKind).nullish(),
    extraction_status: z.array(zExtractionStatus).nullish(),
    priority: z.array(z.enum([
        'P1',
        'P2',
        'P3'
    ])).nullish(),
    flag: z.array(zClauseFlag).nullish(),
    flagged: z.boolean().nullish(),
    doc_uid: z.string(),
    cursor: z.string().max(4096).nullish(),
    limit: z.int().gte(1).nullish()
});

/**
 * Successful Response
 */
export const zClausesListClausesResponse = zPageClauseSummary;

export const zClausesGetClausePath = z.object({
    clause_uid: z.string()
});

export const zClausesGetClauseQuery = z.object({
    context: z.boolean().optional().default(true)
});

/**
 * Successful Response
 */
export const zClausesGetClauseResponse = zClauseDetailOut;

export const zCurvesListCurvesQuery = z.object({
    curve_type: z.array(zCurveType).nullish(),
    source_kind: z.array(zCurveSource).nullish(),
    digitized_by: z.array(zDigitizedBy).nullish(),
    q: z.string().min(1).max(200).nullish(),
    doc_uid: z.string(),
    cursor: z.string().max(4096).nullish(),
    limit: z.int().gte(1).nullish()
});

/**
 * Successful Response
 */
export const zCurvesListCurvesResponse = zPageCurveSummary;

export const zCurvesGetCurveFacetsQuery = z.object({
    doc_uid: z.string()
});

/**
 * Successful Response
 */
export const zCurvesGetCurveFacetsResponse = zCurveFacets;

export const zCurvesGetCurvePath = z.object({
    curve_uid: z.string()
});

/**
 * Successful Response
 */
export const zCurvesGetCurveResponse = zCurveOut;

export const zVocabularyListDefinitionsQuery = z.object({
    q: z.string().min(1).max(200).nullish(),
    prefix: z.string().regex(/^[A-Za-z]$/).nullish(),
    abbreviations: z.boolean().nullish(),
    doc_uid: z.string(),
    cursor: z.string().max(4096).nullish(),
    limit: z.int().gte(1).nullish()
});

/**
 * Successful Response
 */
export const zVocabularyListDefinitionsResponse = zPageDefinitionRow;

export const zVocabularyLookupDefinitionQuery = z.object({
    term: z.string().min(1).max(200),
    doc_uid: z.string()
});

/**
 * Successful Response
 */
export const zVocabularyLookupDefinitionResponse = zDefinitionRow;

export const zVocabularyGetDefinitionPath = z.object({
    definition_uid: z.string()
});

/**
 * Successful Response
 */
export const zVocabularyGetDefinitionResponse = zDefinitionDetail;

export const zCatalogListDocumentsQuery = z.object({
    jurisdiction: z.string().nullish(),
    family_id: z.string().nullish(),
    status: z.array(zDocumentStatus).nullish(),
    as_of: z.iso.date().nullish(),
    include_drafts: z.boolean().optional().default(false)
});

/**
 * Response Catalog List Documents
 *
 * Successful Response
 */
export const zCatalogListDocumentsResponse = z.array(zDocumentRow);

export const zCatalogGetDocumentPath = z.object({
    doc_uid: z.string()
});

/**
 * Successful Response
 */
export const zCatalogGetDocumentResponse = zDocumentOut;

export const zOperationsGetDocumentAuditPath = z.object({
    doc_uid: z.string()
});

export const zOperationsGetDocumentAuditQuery = z.object({
    run_uid: z.string().regex(/^RUN-\d{8}T\d{6}-[0-9a-f]{8}$/).nullish(),
    priority: z.array(z.enum([
        'P1',
        'P2',
        'P3'
    ])).nullish()
});

/**
 * Successful Response
 */
export const zOperationsGetDocumentAuditResponse = zAuditReport;

export const zCurvesListChartsPath = z.object({
    doc_uid: z.string()
});

export const zCurvesListChartsQuery = z.object({
    q: z.string().min(1).max(200).nullish(),
    topic: z.array(zChartTopic).nullish(),
    priority: z.array(z.enum([
        'P1',
        'P2',
        'P3'
    ])).nullish()
});

/**
 * Response Curves List Charts
 *
 * Successful Response
 */
export const zCurvesListChartsResponse = z.array(zChartSummary);

export const zCurvesGetChartPath = z.object({
    chart_key: z.string().regex(/^(?:(?:fig|tab)-[A-Za-z0-9._-]{1,64}|txt-[0-9a-f]{8})$/),
    doc_uid: z.string()
});

/**
 * Successful Response
 */
export const zCurvesGetChartResponse = zChartDetail;

export const zOperationsListCompletenessGapsPath = z.object({
    doc_uid: z.string()
});

export const zOperationsListCompletenessGapsQuery = z.object({
    priority: z.array(z.enum([
        'P1',
        'P2',
        'P3'
    ])).nullish()
});

/**
 * Response Operations List Completeness Gaps
 *
 * Successful Response
 */
export const zOperationsListCompletenessGapsResponse = z.array(zCompletenessGap);

export const zOperationsGetConfidenceHistogramPath = z.object({
    doc_uid: z.string()
});

/**
 * Successful Response
 */
export const zOperationsGetConfidenceHistogramResponse = zConfidenceHistogram;

export const zCatalogListFiguresPath = z.object({
    doc_uid: z.string()
});

export const zCatalogListFiguresQuery = z.object({
    has_curve: z.boolean().nullish(),
    cursor: z.string().max(4096).nullish(),
    limit: z.int().gte(1).nullish()
});

/**
 * Successful Response
 */
export const zCatalogListFiguresResponse = zPageFigureRow;

export const zSourcesGetFigureImageHeaders = z.object({
    'If-None-Match': z.string().nullish()
});

export const zSourcesGetFigureImagePath = z.object({
    figure_key: z.string(),
    doc_uid: z.string()
});

export const zSourcesGetFigureImageQuery = z.object({
    dpi: z.int().gte(72).lte(300).nullish()
});

/**
 * The figure rendered from its page region.
 */
export const zSourcesGetFigureImageResponse = z.string();

export const zOperationsGetDocumentIntegrityPath = z.object({
    doc_uid: z.string()
});

/**
 * Successful Response
 */
export const zOperationsGetDocumentIntegrityResponse = zIntegrityReport;

export const zClausesListPageClausesPath = z.object({
    page: z.int().gte(1),
    doc_uid: z.string()
});

/**
 * Response Clauses List Page Clauses
 *
 * Successful Response
 */
export const zClausesListPageClausesResponse = z.array(zPageClause);

export const zSourcesGetPageHighlightsPath = z.object({
    page: z.int().gte(1),
    doc_uid: z.string()
});

export const zSourcesGetPageHighlightsQuery = z.object({
    req_uid: z.string().nullish(),
    clause_uid: z.string().nullish()
});

/**
 * Successful Response
 */
export const zSourcesGetPageHighlightsResponse = zPageHighlights;

export const zSourcesLocateTextsBody = zLocateRequest;

export const zSourcesLocateTextsPath = z.object({
    page: z.int().gte(1),
    doc_uid: z.string()
});

/**
 * Successful Response
 */
export const zSourcesLocateTextsResponse = zLocateResult;

export const zSourcesGetDocumentPdfHeaders = z.object({
    Range: z.string().nullish(),
    'If-None-Match': z.string().nullish(),
    'If-Range': z.string().nullish()
});

export const zSourcesGetDocumentPdfPath = z.object({
    doc_uid: z.string()
});

/**
 * The source PDF; 206 with Content-Range for a Range request.
 */
export const zSourcesGetDocumentPdfResponse = z.string();

export const zLifecyclePromoteDocumentBody = zPromoteRequest;

export const zLifecyclePromoteDocumentPath = z.object({
    doc_uid: z.string()
});

/**
 * Successful Response
 */
export const zLifecyclePromoteDocumentResponse = zPromoteResult;

export const zCatalogListStandardsPath = z.object({
    doc_uid: z.string()
});

/**
 * Response Catalog List Standards
 *
 * Successful Response
 */
export const zCatalogListStandardsResponse = z.array(zStandardOut);

export const zCatalogGetDocumentStatsPath = z.object({
    doc_uid: z.string()
});

/**
 * Successful Response
 */
export const zCatalogGetDocumentStatsResponse = zDocumentStats;

export const zLifecycleSetDocumentStatusBody = zStatusRequest;

export const zLifecycleSetDocumentStatusPath = z.object({
    doc_uid: z.string()
});

/**
 * Successful Response
 */
export const zLifecycleSetDocumentStatusResponse = zDocumentOut;

export const zCatalogListTablesPath = z.object({
    doc_uid: z.string()
});

export const zCatalogListTablesQuery = z.object({
    cursor: z.string().max(4096).nullish(),
    limit: z.int().gte(1).nullish()
});

/**
 * Successful Response
 */
export const zCatalogListTablesResponse = zPageTableRow;

export const zCatalogGetTablePath = z.object({
    table_key: z.string(),
    doc_uid: z.string()
});

/**
 * Successful Response
 */
export const zCatalogGetTableResponse = zTableOut;

export const zCatalogListThresholdsPath = z.object({
    doc_uid: z.string()
});

/**
 * Response Catalog List Thresholds
 *
 * Successful Response
 */
export const zCatalogListThresholdsResponse = z.array(zPlantTypeThreshold);

export const zClausesGetTocPath = z.object({
    doc_uid: z.string()
});

export const zClausesGetTocQuery = z.object({
    parent: z.string().nullish()
});

/**
 * Response Clauses Get Toc
 *
 * Successful Response
 */
export const zClausesGetTocResponse = z.array(zTocNode);

export const zOperationsListEvaluationsQuery = z.object({
    doc_uid: z.string().nullish()
});

/**
 * Response Operations List Evaluations
 *
 * Successful Response
 */
export const zOperationsListEvaluationsResponse = z.array(zEvaluationReportRef);

export const zExportsExportRequirementsCsvQuery = z.object({
    doc_uid: z.string(),
    section: z.string().nullish(),
    priority: z.array(z.enum([
        'P1',
        'P2',
        'P3'
    ])).optional(),
    group: z.string().nullish(),
    parameter_id: z.string().nullish(),
    modality: z.array(zModality).optional(),
    value_type: z.array(zValueType).optional(),
    flag: z.array(zQualityFlag).optional(),
    flagged: z.boolean().nullish(),
    min_confidence: z.number().gte(0).lte(1).nullish(),
    max_confidence: z.number().gte(0).lte(1).nullish(),
    reference_point: z.array(zReferencePoint).optional(),
    has_curve: z.boolean().nullish(),
    has_open_parameters: z.boolean().nullish(),
    extraction_method: z.array(zExtractionMethod).optional(),
    applies_to_all: z.boolean().nullish(),
    technology: z.array(z.string()).optional(),
    plant_type: z.array(z.string()).optional(),
    topic: z.array(z.string()).optional(),
    control_function: z.array(z.string()).optional(),
    user_category: z.array(z.string()).optional(),
    clause_uid: z.string().nullish(),
    quantitative: z.boolean().nullish(),
    q: z.string().min(1).max(500).nullish()
});

/**
 * Requirements as UTF-8 CSV with BOM (RFC 4180).
 */
export const zExportsExportRequirementsCsvResponse = z.string();

export const zExportsExportRequirementsXlsxQuery = z.object({
    doc_uid: z.string(),
    section: z.string().nullish(),
    priority: z.array(z.enum([
        'P1',
        'P2',
        'P3'
    ])).optional(),
    group: z.string().nullish(),
    parameter_id: z.string().nullish(),
    modality: z.array(zModality).optional(),
    value_type: z.array(zValueType).optional(),
    flag: z.array(zQualityFlag).optional(),
    flagged: z.boolean().nullish(),
    min_confidence: z.number().gte(0).lte(1).nullish(),
    max_confidence: z.number().gte(0).lte(1).nullish(),
    reference_point: z.array(zReferencePoint).optional(),
    has_curve: z.boolean().nullish(),
    has_open_parameters: z.boolean().nullish(),
    extraction_method: z.array(zExtractionMethod).optional(),
    applies_to_all: z.boolean().nullish(),
    technology: z.array(z.string()).optional(),
    plant_type: z.array(z.string()).optional(),
    topic: z.array(z.string()).optional(),
    control_function: z.array(z.string()).optional(),
    user_category: z.array(z.string()).optional(),
    clause_uid: z.string().nullish(),
    quantitative: z.boolean().nullish(),
    q: z.string().min(1).max(500).nullish()
});

/**
 * Requirements workbook (sheets Requirements and About).
 */
export const zExportsExportRequirementsXlsxResponse = z.string();

export const zCatalogListFamiliesQuery = z.object({
    jurisdiction: z.string().nullish()
});

/**
 * Response Catalog List Families
 *
 * Successful Response
 */
export const zCatalogListFamiliesResponse = z.array(zFamilyOut);

export const zCatalogGetCurrentDocumentPath = z.object({
    family_id: z.string()
});

export const zCatalogGetCurrentDocumentQuery = z.object({
    as_of: z.iso.date().nullish()
});

/**
 * Successful Response
 */
export const zCatalogGetCurrentDocumentResponse = zDocumentOut;

export const zLifecycleListFamilyVersionsPath = z.object({
    family_id: z.string()
});

/**
 * Response Lifecycle List Family Versions
 *
 * Successful Response
 */
export const zLifecycleListFamilyVersionsResponse = z.array(zDocumentOut);

export const zFeedbackSendFeedbackBody = zFeedbackCreate;

/**
 * Successful Response
 */
export const zFeedbackSendFeedbackResponse = zFeedbackReceipt;

export const zJobsListJobsQuery = z.object({
    active: z.boolean().nullish(),
    kind: z.array(zJobKind).nullish(),
    doc_uid: z.string().nullish(),
    family_id: z.string().nullish(),
    limit: z.int().gte(1).lte(200).optional().default(50)
});

/**
 * Response Jobs List Jobs
 *
 * Successful Response
 */
export const zJobsListJobsResponse = z.array(zOperationJob);

export const zJobsCreateJobBody = zJobRequest;

export const zJobsCreateJobHeaders = z.object({
    'Idempotency-Key': z.uuid()
});

/**
 * The job created earlier with the same Idempotency-Key.
 */
export const zJobsCreateJobResponse = zOperationJob;

/**
 * Successful Response
 */
export const zJobsGetActiveJobCountResponse = zActiveJobCount;

/**
 * Successful Response
 */
export const zJobsGetJobCapacityResponse = zJobCapacity;

export const zJobsGetJobEstimateQuery = z.object({
    family_id: z.string(),
    version_label: z.string(),
    kind: z.literal('ingest').optional().default('ingest'),
    mode: z.enum(['sync', 'batch']).optional().default('batch'),
    priorities: z.array(z.enum([
        'P1',
        'P2',
        'P3'
    ])).nullish()
});

/**
 * Successful Response
 */
export const zJobsGetJobEstimateResponse = zCostEstimate;

export const zJobsGetJobPath = z.object({
    job_id: z.string()
});

/**
 * Successful Response
 */
export const zJobsGetJobResponse = zOperationJob;

/**
 * Body
 */
export const zJobsCancelJobBody = zCancelJobRequest.nullable();

export const zJobsCancelJobPath = z.object({
    job_id: z.string()
});

/**
 * Successful Response
 */
export const zJobsCancelJobResponse = zOperationJob;

export const zJobsStreamJobEventsHeaders = z.object({
    'Last-Event-ID': z.string().nullish()
});

export const zJobsStreamJobEventsPath = z.object({
    job_id: z.string()
});

/**
 * Response Meta List Jurisdictions
 *
 * Successful Response
 */
export const zMetaListJurisdictionsResponse = z.array(zJurisdictionOut);

/**
 * Successful Response
 */
export const zMetaGetMeResponse = zMe;

/**
 * Successful Response
 */
export const zWhatsNewListEntriesResponse = zWhatsNew;

export const zWhatsNewMarkSeenBody = zWhatsNewSeen;

/**
 * Successful Response
 */
export const zWhatsNewMarkSeenResponse = zWhatsNew;

/**
 * Successful Response
 */
export const zMetaGetMetaResponse = zApiMeta;

export const zVocabularyListParameterGroupsQuery = z.object({
    doc_uid: z.string().nullish()
});

/**
 * Response Vocabulary List Parameter Groups
 *
 * Successful Response
 */
export const zVocabularyListParameterGroupsResponse = z.array(zGroupOut);

export const zVocabularyListParametersQuery = z.object({
    group: z.string().nullish(),
    status: z.array(zParameterStatus).nullish(),
    q: z.string().min(1).max(200).nullish(),
    semantic: z.boolean().optional().default(false),
    doc_uid: z.string().nullish(),
    has_requirements: z.boolean().nullish(),
    sort: zParameterSort.optional().default('requirements'),
    dir: zSortDirection.nullish(),
    cursor: z.string().max(4096).nullish(),
    limit: z.int().gte(1).nullish()
});

/**
 * Successful Response
 */
export const zVocabularyListParametersResponse = zPageParameterRow;

export const zVocabularyGetParameterPath = z.object({
    parameter_id: z.string()
});

export const zVocabularyGetParameterQuery = z.object({
    doc_uid: z.string().nullish()
});

/**
 * Successful Response
 */
export const zVocabularyGetParameterResponse = zParameterDetail;

export const zVocabularyListParameterMarketsPath = z.object({
    parameter_id: z.string()
});

export const zVocabularyListParameterMarketsQuery = z.object({
    jurisdiction: z.array(z.string()).nullish(),
    as_of: z.iso.date().nullish()
});

/**
 * Response Vocabulary List Parameter Markets
 *
 * Successful Response
 */
export const zVocabularyListParameterMarketsResponse = z.array(zMarketRequirement);

export const zRequirementsListRequirementsQuery = z.object({
    doc_uid: z.string(),
    section: z.string().nullish(),
    priority: z.array(z.enum([
        'P1',
        'P2',
        'P3'
    ])).optional(),
    group: z.string().nullish(),
    parameter_id: z.string().nullish(),
    modality: z.array(zModality).optional(),
    value_type: z.array(zValueType).optional(),
    flag: z.array(zQualityFlag).optional(),
    flagged: z.boolean().nullish(),
    min_confidence: z.number().gte(0).lte(1).nullish(),
    max_confidence: z.number().gte(0).lte(1).nullish(),
    reference_point: z.array(zReferencePoint).optional(),
    has_curve: z.boolean().nullish(),
    has_open_parameters: z.boolean().nullish(),
    extraction_method: z.array(zExtractionMethod).optional(),
    applies_to_all: z.boolean().nullish(),
    technology: z.array(z.string()).optional(),
    plant_type: z.array(z.string()).optional(),
    topic: z.array(z.string()).optional(),
    control_function: z.array(z.string()).optional(),
    user_category: z.array(z.string()).optional(),
    clause_uid: z.string().nullish(),
    quantitative: z.boolean().nullish(),
    q: z.string().min(1).max(500).nullish(),
    sort: zRequirementSort.optional().default('document'),
    dir: zSortDirection.optional().default('asc'),
    cursor: z.string().max(4096).nullish(),
    limit: z.int().gte(1).nullish()
});

/**
 * Successful Response
 */
export const zRequirementsListRequirementsResponse = zPageRequirementRow;

export const zRequirementsGetRequirementFacetsQuery = z.object({
    doc_uid: z.string(),
    section: z.string().nullish(),
    priority: z.array(z.enum([
        'P1',
        'P2',
        'P3'
    ])).optional(),
    group: z.string().nullish(),
    parameter_id: z.string().nullish(),
    modality: z.array(zModality).optional(),
    value_type: z.array(zValueType).optional(),
    flag: z.array(zQualityFlag).optional(),
    flagged: z.boolean().nullish(),
    min_confidence: z.number().gte(0).lte(1).nullish(),
    max_confidence: z.number().gte(0).lte(1).nullish(),
    reference_point: z.array(zReferencePoint).optional(),
    has_curve: z.boolean().nullish(),
    has_open_parameters: z.boolean().nullish(),
    extraction_method: z.array(zExtractionMethod).optional(),
    applies_to_all: z.boolean().nullish(),
    technology: z.array(z.string()).optional(),
    plant_type: z.array(z.string()).optional(),
    topic: z.array(z.string()).optional(),
    control_function: z.array(z.string()).optional(),
    user_category: z.array(z.string()).optional(),
    clause_uid: z.string().nullish(),
    quantitative: z.boolean().nullish(),
    q: z.string().min(1).max(500).nullish()
});

/**
 * Successful Response
 */
export const zRequirementsGetRequirementFacetsResponse = zRequirementFacets;

export const zRequirementsGetRequirementPath = z.object({
    req_uid: z.string()
});

/**
 * Successful Response
 */
export const zRequirementsGetRequirementResponse = zRequirementDetail;

export const zOperationsListRunsQuery = z.object({
    doc_uid: z.string().nullish(),
    status: z.array(z.enum([
        'running',
        'succeeded',
        'partial',
        'failed',
        'rolled_back',
        'interrupted'
    ])).nullish(),
    limit: z.int().gte(1).lte(200).optional().default(20)
});

/**
 * Response Operations List Runs
 *
 * Successful Response
 */
export const zOperationsListRunsResponse = z.array(zRunSummary);

export const zOperationsGetRunPath = z.object({
    run_uid: z.string().regex(/^RUN-\d{8}T\d{6}-[0-9a-f]{8}$/)
});

/**
 * Successful Response
 */
export const zOperationsGetRunResponse = zRunDetail;

export const zOperationsGetRunReportPath = z.object({
    run_uid: z.string().regex(/^RUN-\d{8}T\d{6}-[0-9a-f]{8}$/)
});

export const zOperationsGetRunReportQuery = z.object({
    format: z.enum(['json', 'md']).optional().default('json')
});

/**
 * Successful Response
 */
export const zOperationsGetRunReportResponse = zAuditReport;

export const zJobsGetRollbackImpactPath = z.object({
    run_uid: z.string()
});

/**
 * Successful Response
 */
export const zJobsGetRollbackImpactResponse = zRollbackImpact;

export const zSearchSearchClausesQuery = z.object({
    q: z.string().min(1).max(500),
    doc_uid: z.array(z.string()).nullish(),
    jurisdiction: z.array(z.string()).nullish(),
    as_of: z.iso.date().nullish(),
    include_drafts: z.boolean().optional().default(false),
    k: z.int().gte(1).lte(50).nullish()
});

/**
 * Successful Response
 */
export const zSearchSearchClausesResponse = zSearchResponse;

export const zSearchGetOmniboxQuery = z.object({
    q: z.string().min(1).max(200),
    jurisdiction: z.string().nullish(),
    doc_uid: z.string().nullish(),
    limit: z.int().gte(1).lte(20).optional().default(6)
});

/**
 * Successful Response
 */
export const zSearchGetOmniboxResponse = zOmnibox;

/**
 * Response Operations List Sources
 *
 * Successful Response
 */
export const zOperationsListSourcesResponse = z.array(zSourceFamilyOut);

export const zOperationsGetSourceWatchPath = z.object({
    family_id: z.string()
});

/**
 * Successful Response
 */
export const zOperationsGetSourceWatchResponse = zSourceWatchOut;

/**
 * Successful Response
 */
export const zOperationsGetSystemStatusResponse = zSystemStatus;

export const zLifecycleGetVersionDiffQuery = z.object({
    old: z.string(),
    new: z.string()
});

/**
 * Successful Response
 */
export const zLifecycleGetVersionDiffResponse = zVersionDiffOut;

export const zLifecycleGetClauseContextDiffQuery = z.object({
    old: z.string(),
    new: z.string(),
    number: z.string(),
    old_number: z.string().nullish()
});

/**
 * Successful Response
 */
export const zLifecycleGetClauseContextDiffResponse = zClauseContextDiffOut;

/**
 * Successful Response
 */
export const zMetaGetHealthResponse = zHealth;

/**
 * Successful Response
 */
export const zMetaGetReadinessResponse = zReadiness;
